import { baseApiClient as api } from "../baseService";

export const addTagTypes = ["cms-updates"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      cmsUpdatesList: build.query<
        CmsUpdatesListApiResponse,
        CmsUpdatesListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/cms-updates/`,
          params: {
            status: queryArg.status,
          },
        }),
        providesTags: ["cms-updates"],
      }),
      cmsUpdatesMarkSkipped: build.mutation<
        CmsUpdatesMarkSkippedApiResponse,
        CmsUpdatesMarkSkippedApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/cms-updates/${queryArg.id}/mark-skipped/`,
          method: "POST",
        }),
        invalidatesTags: ["cms-updates"],
      }),
      cmsUpdatesMarkTransferred: build.mutation<
        CmsUpdatesMarkTransferredApiResponse,
        CmsUpdatesMarkTransferredApiArg
      >({
        query: (queryArg) => ({
          url: `/api/matters/${queryArg.matterId}/cms-updates/${queryArg.id}/mark-transferred/`,
          method: "POST",
        }),
        invalidatesTags: ["cms-updates"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type CmsUpdatesListApiResponse =
  /** status 200  */ DomainEventUpdateItemDetailedRead[];
export type CmsUpdatesListApiArg = {
  matterId: string;
  /** Statuses to filter by */
  status?: ("filtered" | "pending" | "skipped" | "transferred")[];
};
export type CmsUpdatesMarkSkippedApiResponse =
  /** status 200  */ DomainEventUpdateItemDetailedRead;
export type CmsUpdatesMarkSkippedApiArg = {
  id: string;
  matterId: string;
};
export type CmsUpdatesMarkTransferredApiResponse =
  /** status 200  */ DomainEventUpdateItemDetailedRead;
export type CmsUpdatesMarkTransferredApiArg = {
  id: string;
  matterId: string;
};
export type ArtifactType =
  | "TRANSCRIPT"
  | "AUDIO_CONVERSATION"
  | "DOCUMENT"
  | "EMAIL"
  | "EMAIL_ATTACHMENT"
  | "INTAKE_NOTE"
  | "MESSAGE"
  | "MESSAGE_ATTACHMENT"
  | "CONVERSATION"
  | "BLOB"
  | "STAFF_NOTE";
export type ArtifactPrimaryCategory =
  | "Police Report and Evidence"
  | "Insurance Information"
  | "Medical Records"
  | "Bills and Liens"
  | "Client Correspondence"
  | "Unclear";
export type ArtifactDigestSchema = {
  id: string | string;
  artifact_id: string | string;
  title?: string | null;
  brief?: string | null;
  primary_tag?: ArtifactPrimaryCategory;
  secondary_tags?: string[] | null;
  source?: string | null;
  information_score?: number | null;
  work_log_entry?: string | null;
  client_name?: string | null;
  confidence?: number | null;
  long_form_description?: string | null;
  three_line_summary?: string | null;
  one_line_summary?: string | null;
  file_name?: string | null;
  subtitle?: string | null;
  blur_by_default?: boolean | null;
  status?: string | null;
};
export type ConversationSource = "twilio" | "dialpad" | "bland_ai";
export type AudioConversationSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  name?: string | null;
  start_timestamp?: string | null;
  end_timestamp?: string | null;
  duration?: number | null;
  is_inbound?: boolean | null;
  attorney_number?: string | null;
  client_number?: string | null;
  twilio_number?: string | null;
  twilio_sid_incoming_call?: string | null;
  dialpad_call_id?: string | null;
  source?: ConversationSource;
};
export type TranscriptSchema = {
  id: string | string;
  transcript_path?: string | null;
  conversation?: AudioConversationSchema;
  created_at: string;
  modified_at: string;
};
export type BlobSchema = {
  id: string | string;
  name: string;
  original_filename: string | null;
  collection_id: string | string;
  collection_name: string;
  file_hash: string;
  file_size: number;
  file_extension: string | null;
  file_mime_type: string | null;
  download_url?: string | null;
  created_at: string;
  modified_at: string;
};
export type MessageType =
  | "sms"
  | "mms"
  | "call"
  | "custom"
  | "email"
  | "facebook"
  | "front_chat"
  | "googleplay"
  | "intercom"
  | "internal"
  | "phone-call"
  | "tweet"
  | "tweet_dm"
  | "whatsapp"
  | "yalo_wha"
  | "fax"
  | "mailed_letter"
  | "mailed_cheque";
export type MessageSource =
  | "dialpad"
  | "front"
  | "hellosign_fax"
  | "postgrid"
  | "finch_correspondence";
export type MessageAttachmentSchema = {
  id: string | string;
  name: string | null;
  content_type: string | null;
  file_size: number | null;
  description: string | null;
  blob: BlobSchema;
};
export type RecipientRole = "from" | "to" | "cc" | "bcc" | "reply_to";
export type BasicRecipientSchema = {
  name?: string | null;
  handle?: string | null;
  role?: RecipientRole;
  external_id?: string | null;
};
export type BasicRecipientBlob = {
  recipients: BasicRecipientSchema[];
};
export type TransmissionStatus =
  | "pending"
  | "sending"
  | "sent"
  | "delivered"
  | "failed"
  | "bounced";
export type MessageSchema = {
  id: string | string;
  message_type: MessageType;
  content: string | null;
  subject: string | null;
  blurb: string | null;
  text: string | null;
  is_inbound: boolean | null;
  from_number: string | null;
  to_numbers: string[] | null;
  source: MessageSource;
  source_timestamp: string | null;
  created_at: string;
  modified_at: string;
  attachments: MessageAttachmentSchema[];
  recipient_blob?: BasicRecipientBlob;
  transmission_status?: TransmissionStatus;
  transmission_error?: string | null;
};
export type ConversationSchema = {
  id: string | string;
  is_client_sms_conversation: boolean;
  created_at: string;
  modified_at: string;
  messages: MessageSchema[];
};
export type StaffNoteSchema = {
  content: string;
  matter_id: string | string;
  id: string | string;
  user_id: string | string;
  created_at: string;
  modified_at: string;
  agent_tags?: string[];
};
export type DetailedArtifactSchema = {
  id: string | string;
  matter_id: string | string;
  name: string;
  artifact_type: ArtifactType;
  created_at: string;
  modified_at: string;
  extract_from?: boolean;
  artifact_digest?: ArtifactDigestSchema;
  audio_conversation?: AudioConversationSchema;
  transcript?: TranscriptSchema;
  blob?: BlobSchema;
  conversation?: ConversationSchema;
  message?: MessageSchema;
  staff_note?: StaffNoteSchema;
};
export type ArtifactCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact: DetailedArtifactSchema;
};
export type CustomUserSchema = {
  id: string | string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  is_firm_admin?: boolean;
  avatar?: string | null;
  signature?: string | null;
};
export type AgentType =
  | "ingest_transcript_task"
  | "provider_finder"
  | "artifact_digest"
  | "treatment_researcher"
  | "treatment_request_automation"
  | "bland_caller"
  | "slack_task_bot"
  | "blob_sorter"
  | "front_app_integration"
  | "task_generator"
  | "coverage_extractor"
  | "intake_converter"
  | "airtable_sync"
  | "system"
  | "backfill_script"
  | "domain_event_update_orchestrator"
  | "loa_detector"
  | "rainmaker"
  | "deliverable_sync"
  | "bpo_agent"
  | "loa_chaser"
  | "external_task_rule_engine"
  | "external_task_bulk_create"
  | "entity_sync"
  | "case_activation";
export type DiscriminatedArtifactCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "artifact_created";
  payload: ArtifactCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ArtifactDigestCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact_id: string | string;
  title: string;
  status: string;
  artifact: DetailedArtifactSchema;
};
export type DiscriminatedArtifactDigestCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "artifact_digest_created";
  payload: ArtifactDigestCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ArtifactDigestStatusUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact_id: string | string;
  old_status: string;
  new_status: string;
};
export type DiscriminatedArtifactDigestStatusUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "artifact_digest_status_updated";
  payload: ArtifactDigestStatusUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type BlobCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  blob: BlobSchema;
};
export type DiscriminatedBlobCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "blob_created";
  payload: BlobCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type BlobDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  blob: BlobSchema;
};
export type DiscriminatedBlobDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "blob_deleted";
  payload: BlobDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PolicyType =
  | "AUTO"
  | "HOMEOWNERS"
  | "COMMERCIAL"
  | "HEALTH"
  | "UMBRELLA"
  | "WORKERS_COMPENSATION"
  | "GENERAL_LIABILITY"
  | "OTHER";
export type ContactHandleType =
  | "PHONE"
  | "EMAIL"
  | "WEBSITE"
  | "FAX"
  | "OTHER"
  | "MAILING_ADDRESS";
export type WebSource = {
  /** The URL of the source */
  url: string;
  /** Very short display text for the source URL */
  title: string;
};
export type ContactHandleSchema = {
  id?: (string | string) | null;
  /** The captured contact information. Should contain an E.164 formatted phone number, or an email address, or a website, or a fax number. */
  value?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  handle_type: ContactHandleType;
  /** Short description of the handle; e.g. 'Billing Dept Toll-Free', 'Front Desk Fax' etc. */
  name?: string | null;
  /** Optional notes about the handle. */
  notes?: string | null;
  /** Citations for the handle. */
  sources?: WebSource[] | null;
  /** Whether this is the preferred contact handle. */
  preferred?: boolean | null;
  division_id?: (string | string) | null;
};
export type CarrierDetail = {
  /**
            The colloquial, common form, well-known name of the insurance carrier if mentioned, null otherwise.
            This is a key field for matching and deduplication.
             */
  name?: string | null;
  /**
            The BEST (at _MOST_ 2 handles per type) web pages or phone numbers for submitting and managing claims if mentioned, null otherwise.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            The headquarters address of the insurance carrier if mentioned, null otherwise.
             */
  address?: string | null;
};
export type IdentifierType =
  | "POLICY_NUMBER"
  | "MEMBER_ID"
  | "GROUP_NUMBER"
  | "OTHER";
export type PolicyIdentifier = {
  /**
            The type of identifier for the policy.
             */
  identifier_type: IdentifierType;
  /**
            The value of the identifier for the policy.
             */
  identifier_value: string;
};
export type BenefitType =
  | "PERSONAL_INJURY_PROTECTION"
  | "MEDICAL_PAYMENTS"
  | "UNINSURED_MOTORIST"
  | "UNDER_INSURED_MOTORIST"
  | "BODILY_INJURY_LIABILITY"
  | "PROPERTY_DAMAGE_LIABILITY"
  | "COMPREHENSIVE_AUTO"
  | "COLLISION_AUTO"
  | "WORKERS_COMPENSATION"
  | "OTHER";
export type BenefitDetail = {
  /**
            A limited categorization of insurance benefits we are interested in extracting.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType;
  /**
            Structured map of coverage limitation labels to their numeric dollar amounts.
            Keys describe the limit type (e.g., "per person", "per accident", "bodily injury per person").
            Values MUST be integers representing dollar amounts (e.g., 25000 for $25,000).
    
            IMPORTANT: Do NOT use slash notation like "25000/50000". Instead, create SEPARATE entries:
            - WRONG: {"bodily injury": "25000/50000"}
            - CORRECT: {"bodily injury per person": 25000, "bodily injury per accident": 50000}
    
            Examples:
            - {"per person": 100000, "per accident": 300000}
            - {"BI per person": 25000, "BI per accident": 50000, "PD": 25000}
            - {"medical payments": 5000}
             */
  limitations?: {
    [key: string]: number;
  } | null;
  /**
            Whether stacking benefits apply for UM/UIM coverage. When true, coverage limits from
            multiple policies can be combined. Only applicable for UNINSURED_MOTORIST and
            UNDER_INSURED_MOTORIST benefit types. Null indicates unknown/not specified.
             */
  is_stacked?: boolean | null;
};
export type CoverageDataSource =
  | "UNKNOWN"
  | "BPO"
  | "USER_EDIT"
  | "SYSTEM"
  | "VOICE_AI";
export type CoverageDataSourceInfo = {
  /** The source that created or last modified this data. */
  source?: CoverageDataSource;
  /** User ID if source is USER_EDIT. */
  user_id?: string | null;
  /** User display name if source is USER_EDIT (for display purposes). */
  user_name?: string | null;
  /** When the data was added/modified from this source. */
  timestamp?: string | null;
};
export type AdjusterDetail = {
  /**
            The benefit the adjuster is specifically assigned to, if explicitly stated, otherwise null.
             */
  benefit_type?: BenefitType;
  /**
            Name of the adjuster handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the adjuster (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            Metadata about the source of this adjuster data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type ClaimDetail = {
  /**
            The benefit against which the claim is being made, if explicitly stated, otherwise null.
            
            <benefits-type-glossary>
                PIP == PERSONAL_INJURY_PROTECTION
                MEDPAY == MEDICAL_PAYMENTS
                UM == UNINSURED_MOTORIST
                UIM == UNDER_INSURED_MOTORIST
                BI == BODILY_INJURY_LIABILITY
                COMPREHENSIVE == COMPREHENSIVE_AUTO
                COLLISION == COLLISION_AUTO
                anything else == OTHER
            </benefits-type-glossary>
              
             */
  benefit_type?: BenefitType;
  /**
            The claim identifier (number, id, etc.) for the policy, if explicitly stated, otherwise null.
             */
  claim_identifier?: string | null;
  /**
            Metadata about the source of this claim data (BPO, user edit, system, etc.).
            None indicates unknown/legacy data for backwards compatibility.
             */
  source?: CoverageDataSourceInfo;
};
export type VehicleDetail = {
  /**
            The make of the vehicle if explicitly stated, otherwise null.
             */
  make?: string | null;
  /**
            The model of the vehicle if explicitly stated, otherwise null.
             */
  model?: string | null;
  /**
            The year of the vehicle if explicitly stated, otherwise null.
             */
  year?: number | null;
  /**
            The VIN of the vehicle if explicitly stated, otherwise null.
             */
  vin?: string | null;
  /**
            The license plate of the vehicle if explicitly stated, otherwise null.
             */
  license_plate?: string | null;
  /**
            The color of the vehicle if explicitly stated, otherwise null.
             */
  color?: string | null;
};
export type PolicyDetail = {
  /**
            A well formatted label for the policy detail, i.e "VIN", "Vehicle Description", "Premium", "Insured Address", "Garage Address"
             */
  key: string;
  /**
            The value of the policy detail, i.e "1234567890", "Toyota Camry", "100000.00"
             */
  value: string;
};
export type SubrogatorDetail = {
  /**
            Name of the subrogator handling a related claim if explicitly stated, otherwise null.
             */
  name?: string | null;
  /**
            The BEST (at MOST 2 handles per type) contact methods for the subrogator (phone, email, fax) if explicitly stated,
            otherwise null.
             */
  handles?: ContactHandleSchema[] | null;
  /**
            List of sources that provided the subrogator information if explicitly stated, otherwise null.
             */
  sources?: WebSource[] | null;
};
export type PropertyContactType =
  | "PROPERTY_OWNER"
  | "PROPERTY_MANAGER"
  | "OTHER";
export type PropertyContact = {
  /** The type of contact (owner, manager, or other). */
  contact_type: PropertyContactType;
  /** Custom label when contact_type is OTHER (e.g., 'Tenant', 'Security Company'). */
  custom_type_label?: string | null;
  /** Name of the contact person or entity. */
  name?: string | null;
  /** Contact methods: phone, email, fax, etc. */
  handles?: ContactHandleSchema[] | null;
  /** Mailing address for the contact. */
  address?: string | null;
  /** Additional notes about this contact. */
  notes?: string | null;
};
export type CoverageStatus =
  | "NEEDS_APPROVAL"
  | "APPROVED"
  | "REJECTED"
  | "NOT_APPLICABLE";
export type CoverageLiabilityStatus =
  | "ACCEPTED"
  | "REJECTED"
  | "PARTIALLY_ACCEPTED"
  | "LIABILITY_UNKNOWN"
  | "UNDER_INVESTIGATION";
export type CoverageCategory =
  | "FIRST_PARTY"
  | "FIRST_PARTY_HEALTH"
  | "THIRD_PARTY"
  | "NOT_LIABLE_INVOLVED_PARTY"
  | "UNCLEAR"
  | "PROPERTY"
  | "WORKERS_COMPENSATION";
export type CoverageMentionStubSchema = {
  id: string | string;
  created_at: string;
  artifact_id: string | string;
  artifact_type: ArtifactType;
  citation_ids?: string[] | null;
  confidence?: number | null;
  context?: string | null;
};
export type EnrichmentSource =
  | "perplexity"
  | "cached"
  | "manual"
  | "intake"
  | "extraction";
export type EnrichmentMetadata = {
  source_matter_id?: string | null;
  source_matter_name?: string | null;
  match_reason?: string | null;
  cached_at?: string | null;
  confidence_score?: number | null;
  [key: string]: any;
};
export type LorUploadedArtifactSummary = {
  id: string | string;
  blob_id?: (string | string) | null;
  name?: string | null;
  original_filename?: string | null;
  file_extension?: string | null;
  file_mime_type?: string | null;
  created_at?: string | null;
};
export type OpenClaimFieldStatus =
  | "VERIFIED"
  | "SUGGESTED"
  | "MISSING"
  | "UNKNOWN"
  | "WRONG";
export type OpenClaimCitedFieldStr = {
  /** The discovered or existing value for this field. */
  value?: string | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: (string | string) | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimClientInfo = {
  /** Client's full legal name. */
  name?: OpenClaimCitedFieldStr;
  /** Client's contact phone number. */
  phone?: OpenClaimCitedFieldStr;
  /** Client's email address. */
  email?: OpenClaimCitedFieldStr;
  /** Client's current mailing address (street address). */
  address?: OpenClaimCitedFieldStr;
  /** Client's city. */
  city?: OpenClaimCitedFieldStr;
  /** Client's ZIP code. */
  zip_code?: OpenClaimCitedFieldStr;
  /** Client's date of birth (MM/DD/YYYY). */
  dob?: OpenClaimCitedFieldStr;
  /** Last four digits of client's Social Security Number. */
  last_four_ssn?: OpenClaimCitedFieldStr;
};
export type OpenClaimIncidentInfo = {
  /** Date the incident occurred (MM/DD/YYYY). */
  date_of_loss?: OpenClaimCitedFieldStr;
  /** Time the incident occurred (e.g., 2:30 PM or 14:30). */
  time_of_loss?: OpenClaimCitedFieldStr;
  /** Location where the incident occurred (street address or intersection). */
  location?: OpenClaimCitedFieldStr;
  /** Weather conditions at time of incident. */
  weather_conditions?: OpenClaimCitedFieldStr;
  /** Brief description of how the incident occurred. */
  incident_description?: OpenClaimCitedFieldStr;
};
export type OpenClaimCitedFieldUnionIntNoneType = {
  /** The discovered or existing value for this field. */
  value?: number | null;
  /** The verification status of this field. */
  status?: OpenClaimFieldStatus;
  /** Confidence score (0-1) for the accuracy of this value. */
  confidence?: number | null;
  /** Citation IDs linking to source text in documents. */
  citation_ids?: string[];
  /** Artifact ID of the primary source document. */
  source_artifact_id?: (string | string) | null;
  /** Brief explanation of why this value was suggested or verified. */
  reasoning?: string | null;
};
export type OpenClaimVehicleInfo = {
  /** Vehicle model year. */
  year?: OpenClaimCitedFieldUnionIntNoneType;
  /** Vehicle manufacturer (e.g., Toyota, Ford). */
  make?: OpenClaimCitedFieldStr;
  /** Vehicle model name (e.g., Camry, F-150). */
  model?: OpenClaimCitedFieldStr;
  /** Vehicle Identification Number. */
  vin?: OpenClaimCitedFieldStr;
  /** License plate number and state. */
  license_plate?: OpenClaimCitedFieldStr;
  /** Vehicle color. */
  color?: OpenClaimCitedFieldStr;
  /** UUID of the coverage this vehicle should be assigned to. */
  suggested_coverage_id?: OpenClaimCitedFieldStr;
};
export type OpenClaimCoverageInfo = {
  /** Name of the insurance carrier. */
  carrier_name?: OpenClaimCitedFieldStr;
  /** Policy number on the insurance card/documents. */
  policy_number?: OpenClaimCitedFieldStr;
  /** Claims phone number for the carrier. */
  carrier_phone?: OpenClaimCitedFieldStr;
  /** Name of the policyholder/named insured. */
  named_insured?: OpenClaimCitedFieldStr;
  /** Existing claim number if a claim has already been filed. */
  claim_number?: OpenClaimCitedFieldStr;
};
export type OpenClaimAtFaultDriverInfo = {
  /** Name of the at-fault or other involved driver. */
  name?: OpenClaimCitedFieldStr;
  /** Phone number of the at-fault driver. */
  phone?: OpenClaimCitedFieldStr;
  /** Address of the at-fault driver. */
  address?: OpenClaimCitedFieldStr;
};
export type OpenClaimContextSchema = {
  /** Client/claimant information. */
  client?: OpenClaimClientInfo;
  /** Details about the incident/accident. */
  incident?: OpenClaimIncidentInfo;
  /** Client's vehicle(s) involved in the incident. */
  client_vehicles?: OpenClaimVehicleInfo[];
  /** Insurance coverage details. */
  coverage?: OpenClaimCoverageInfo;
  /** At-fault or other party driver information. */
  at_fault_driver?: OpenClaimAtFaultDriverInfo;
  /** At-fault or other party vehicle information. */
  defendant_vehicle?: OpenClaimVehicleInfo;
  /** ISO timestamp when AI discovery completed. */
  discovery_completed_at?: string | null;
  /** Summary of what was found and what's missing. */
  discovery_summary?: string | null;
};
export type CoverageSchema = {
  /**
            The type of policy if explicitly stated, otherwise null.
             */
  policy_type?: PolicyType;
  /**
            Details about the insurance carrier if mentioned, otherwise null.
             */
  carrier?: CarrierDetail;
  /**
            Carrier-issued identifiers for the policy, if explicitly stated, otherwise null.
             */
  policy_identifiers?: PolicyIdentifier[] | null;
  /**
            List of names of primary policyholders or insured entities if stated or implied, otherwise null.
             */
  named_insured?: string[] | null;
  /**
            List of benefits covered by the policy, if explicitly stated, otherwise null.
             */
  benefits?: BenefitDetail[] | null;
  /**
            Any known adjusters handling claims against this policy, if explicitly stated, otherwise null.
             */
  adjusters?: AdjusterDetail[] | null;
  /**
            Any known claims opened against the policy, if explicitly stated, otherwise null.
             */
  claims?: ClaimDetail[] | null;
  /**
            List of vehicles covered by the policy, if applicable and explicitly stated, otherwise null.
             */
  vehicles?: VehicleDetail[] | null;
  /**
            Structured list of extracted policy details, such as, but not limited to:
            VIN; vehicle description; premium amount; insured's address;
            garage location; policy endorsements; coverage exclusions;
            other policy-specific information for claims processing.
             */
  policy_details?: PolicyDetail[] | null;
  /**
            Details about the subrogator if health insurance policy + carrier ... if explicitly stated, otherwise null.
             */
  subrogator?: SubrogatorDetail;
  /**
            DEPRECATED: Use Property.owners instead. Owner/manager information is now stored via
            Organization parties linked to the Property subject through Ownership records.
            This field is retained for backward compatibility with existing data but is no longer
            populated for new coverages.
             */
  property_contacts?: PropertyContact[] | null;
  id: string | string;
  status?: CoverageStatus;
  liability_status?: CoverageLiabilityStatus;
  category?: CoverageCategory;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  /**
            List of mentions of this coverage in the matter, if any.
             */
  mentions?: CoverageMentionStubSchema[] | null;
  carrier_v2_id?: (string | string) | null;
  carrier_division_id?: (string | string) | null;
  /** Source of enrichment data for carrier information */
  enrichment_source?: EnrichmentSource;
  /** Metadata about enrichment source (source matter ID, match reason, etc.) */
  enrichment_metadata?: EnrichmentMetadata;
  /** Whether cached data was used for enrichment */
  cache_used?: boolean;
  lor_document_id?: (string | string) | null;
  lor_uploaded_artifact_id?: (string | string) | null;
  lor_uploaded_artifact?: LorUploadedArtifactSummary;
  /** AI-discovered context for opening a claim with citations and confidence scores. */
  open_claim_context?: OpenClaimContextSchema;
  created_by_user_id?: (string | string) | null;
  created_by_user_name?: string | null;
  /** For PROPERTY category: UUID of the linked Property subject. */
  property_id?: (string | string) | null;
};
export type CoverageCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  needs_approval: boolean;
};
export type DiscriminatedCoverageCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_created";
  payload: CoverageCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CarrierLinkContext = {
  carrier_id: string | string;
  carrier_name: string;
  division_id?: (string | string) | null;
  division_name?: string | null;
  reasoning?: string | null;
  match_method?: string | null;
};
export type CoverageUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  diff: {
    [key: string]: any;
  };
  previous?: CoverageSchema;
  carrier_link_context?: CarrierLinkContext;
};
export type DiscriminatedCoverageUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_updated";
  payload: CoverageUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CoverageStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  old_status: CoverageStatus;
  new_status: CoverageStatus;
};
export type DiscriminatedCoverageStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_status_changed";
  payload: CoverageStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CoverageCategoryChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  old_category: CoverageCategory;
  new_category: CoverageCategory;
};
export type DiscriminatedCoverageCategoryChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_category_changed";
  payload: CoverageCategoryChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CoverageModificationBlockedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  coverage: CoverageSchema;
  operation_type: string;
  workflow_type: string;
  blocked_reason: string;
  recommendation?: {
    [key: string]: any;
  } | null;
};
export type DiscriminatedCoverageModificationBlockedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "coverage_modification_blocked";
  payload: CoverageModificationBlockedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DataRequirementName =
  | "client_name"
  | "client_date_of_birth"
  | "client_last_four_of_ssn"
  | "client_address"
  | "firm_full_name"
  | "date_of_loss"
  | "incident_location"
  | "matter_city"
  | "matter_state"
  | "sanitized_incident_description"
  | "insurance_carrier"
  | "insurance_policy_number"
  | "insurance_claim_number"
  | "insurance_adjuster_name"
  | "other_driver_name"
  | "other_driver_insurance_carrier"
  | "other_driver_insurance_policy_number"
  | "medical_provider_name"
  | "medical_treatment_date";
export type DataRequirementValidationCriteria = {
  expected_format?: string | null;
  validation_prompt?: string | null;
  required_fields?: string[] | null;
};
export type CanonicalModel =
  | "matter"
  | "client"
  | "firm"
  | "insurance_coverage"
  | "medical_provider"
  | "opposing_party";
export type DataRequirementStatus = "Pending" | "Satisfied" | "Bypassed";
export type DataRequirementAgentContext = {
  rationale?: string | null;
};
export type DataRequirementValueStatus = "Extracted" | "Approved" | "Rejected";
export type DataRequirementValueContext = {
  rationale?: string | null;
  extraction_source?: string | null;
  artifact_id?: (string | string) | null;
  confidence_notes?: string | null;
  /** Up to three verbatim snippets from the text that support the extracted value. Include enough leading context to be self-explanatory.  */
  excerpts?: string[];
  /**
            Short, comprehensive sentence, succinctly describing where this data was found in the document.
             */
  extraction_context?: string | null;
  /**
            If there are "citation-id" blocks in the text that correspond to the excerpts, list the IDs here.
             */
  citation_ids?: string[] | null;
};
export type DataRequirementValueSchema = {
  value: string;
  status: DataRequirementValueStatus;
  confidence?: number | null;
  context?: DataRequirementValueContext;
  id: string | string;
  data_requirement_id: string | string;
  created_at: string;
  modified_at: string;
  user_id?: (string | string) | null;
  agent?: AgentType;
};
export type DataRequirementSchema = {
  name: DataRequirementName;
  display_name: string;
  description?: string | null;
  prompt: string;
  validation_criteria?: DataRequirementValidationCriteria;
  canonical_model?: CanonicalModel;
  canonical_field?: string | null;
  status?: DataRequirementStatus;
  id: string | string;
  task_id: string | string;
  created_at: string;
  modified_at: string;
  user_id?: (string | string) | null;
  agent?: AgentType;
  agent_context?: DataRequirementAgentContext;
  values?: DataRequirementValueSchema[];
  canonical_value?: string | null;
};
export type DataRequirementCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement: DataRequirementSchema;
  task_id: string | string;
  name: string;
  display_name: string;
};
export type DiscriminatedDataRequirementCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "data_requirement_created";
  payload: DataRequirementCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DataRequirementStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement: DataRequirementSchema;
  old_status: DataRequirementStatus;
  new_status: DataRequirementStatus;
};
export type DiscriminatedDataRequirementStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "data_requirement_status_changed";
  payload: DataRequirementStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DataRequirementSatisfiedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement: DataRequirementSchema;
  task_id: string | string;
  satisfied_by_value_id: (string | string) | null;
};
export type DiscriminatedDataRequirementSatisfiedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "data_requirement_satisfied";
  payload: DataRequirementSatisfiedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DataRequirementValueCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement_value: DataRequirementValueSchema;
  data_requirement_id: string | string;
  value: string;
  status: DataRequirementValueStatus;
};
export type DiscriminatedDataRequirementValueCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "data_requirement_value_created";
  payload: DataRequirementValueCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DataRequirementValueApprovedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement_value: DataRequirementValueSchema;
  data_requirement_id: string | string;
  value: string;
};
export type DiscriminatedDataRequirementValueApprovedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "data_requirement_value_approved";
  payload: DataRequirementValueApprovedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DataRequirementValueStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  data_requirement_value: DataRequirementValueSchema;
  data_requirement_id: string | string;
  old_status: DataRequirementValueStatus;
  new_status: DataRequirementValueStatus;
};
export type DiscriminatedDataRequirementValueStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "data_requirement_value_status_changed";
  payload: DataRequirementValueStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DeliverableArtifactCriteria = {
  search_keywords?: string[] | null;
  exclusion_keywords?: string[] | null;
};
export type DeliverableAnchors = {
  facts?: {
    [key: string]: string;
  };
};
export type DeliverableStatus = "Pending" | "Satisfied" | "Bypassed";
export type TaskGroups =
  | "Intake"
  | "Police Report & Investigation"
  | "1P Insurance"
  | "3P Insurance"
  | "Medical Records"
  | "Treatment"
  | "Demand Letter"
  | "Liens"
  | "Miscellaneous"
  | "Additional Insurance"
  | "Initial Medical Records";
export type TaskType =
  | "Medical Records"
  | "Medical Bills"
  | "LOR Acknowledgement"
  | "Send LOR"
  | "Open Claim"
  | "Final Lien"
  | "Lien Acknowledgement"
  | "Police Report"
  | "Police Report Escalation"
  | "Freedom of Information Act"
  | "Incident Report"
  | "Open File"
  | "Find Health Insurance Coverage"
  | "Locate Report Source"
  | "Ascertain Subrogator"
  | "Finalize Demand"
  | "Draft Demand"
  | "Demand Readiness Review"
  | "Declaration Page"
  | "Health Insurance Card"
  | "Treatment Check-In"
  | "Treatment Barrier"
  | "HIPAA Directive"
  | "Retainer Agreement"
  | "Conduct Intake"
  | "Conduct Initial Review"
  | "Welcome Call"
  | "Other"
  | "All"
  | "Review Found Coverage"
  | "Review Found Provider"
  | "Update External CMS"
  | "Find 1P Coverage"
  | "Find 3P Coverage"
  | "Client Communications"
  | "Managing Paralegal Asks"
  | "Manage MedPay"
  | "Send MedPay Letter"
  | "Get Liability Limits"
  | "Request Updated PIP Ledger"
  | "Send Evidence Preservation Letter"
  | "Document Collection"
  | "Review 1P Benefits"
  | "PIP/MedPay Application"
  | "Check for PIP Ledger"
  | "IME Required"
  | "UM/UIM Indicate Stack vs. No Stack"
  | "Generate No Medpay Letter"
  | "Provide PIP Info to Medical Provider"
  | "Liability Other"
  | "1P Insurance Other"
  | "3P Insurance Other"
  | "Liens Other"
  | "Treatment Other"
  | "Medical Records Other"
  | "Demand Letter Other"
  | "Confirm Incident Location"
  | "Research Property & Owners";
export type TaskUpdateType = "comment" | "activity";
export type TaskUpdateVisibility = "Private" | "Public";
export type TaskUpdateSchema = {
  id: string | string;
  task_id: string | string;
  update: string;
  update_type: TaskUpdateType;
  visibility: TaskUpdateVisibility;
  user?: CustomUserSchema;
  agent?: AgentType;
  agent_tags?: string[];
  correlation_id?: (string | string) | null;
  created_at: string;
  modified_at: string;
};
export type TaskExternalCmsUpdateStatus = "NOT_UPDATED" | "UPDATED";
export type TaskWithUpdatesSchema = {
  id: string | string;
  name?: string | null;
  description?: string | null;
  group: TaskGroups;
  type: TaskType;
  due_date?: string | null;
  updates: TaskUpdateSchema[];
  external_cms_update_status?: TaskExternalCmsUpdateStatus;
  external_cms_update_reason?: string | null;
};
export type FirmStatus =
  | "Active"
  | "Testing"
  | "Software Only"
  | "New Lead"
  | "Open"
  | "Closed";
export type FirmType = "Solo" | "Small" | "Medium" | "Enterprise";
export type TimeZone = "PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST";
export type CmsSystem =
  | "CASEpeer"
  | "Filevine"
  | "Litify"
  | "MyCase"
  | "Clio Manage"
  | "SmartAdvocate"
  | "PracticePanther"
  | "Needles"
  | "Neos"
  | "Merus"
  | "TrialWorks";
export type LeadSystem =
  | "LeadDocket"
  | "LawProNation"
  | "ClioGrow"
  | "Intaker"
  | "CASEpeer"
  | "Filevine"
  | "Litify"
  | "MyCase"
  | "Clio Manage"
  | "SmartAdvocate"
  | "PracticePanther"
  | "Needles"
  | "Neos"
  | "Merus"
  | "TrialWorks";
export type IntakePreferenceSection = {
  title: string;
  content: string;
};
export type QualificationAction = "accept" | "reject" | "escalate";
export type QualificationCriteria = {
  /** Maximum days from today for date of loss. None means no restriction. */
  date_of_loss_max_days?: number | null;
  /** List of 2-letter US state codes. Empty means all states accepted. */
  accepted_states?: string[];
  /** Per case type actions. Missing types use default action. */
  case_type_actions?: {
    [key: string]: QualificationAction;
  };
  /** Default action for unconfigured case types. */
  default_case_type_action?: QualificationAction;
};
export type IntakePreferences = {
  sections?: IntakePreferenceSection[];
  /** Qualification criteria for intake decision support. */
  qualification_criteria?: QualificationCriteria;
};
export type FirmSchema = {
  id: string;
  name: string;
  full_name?: string | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  status?: FirmStatus;
  type?: FirmType;
  is_paying?: boolean;
  paying_since?: string | null;
  time_zone?: TimeZone;
  primary_state?: string | null;
  managing_paralegal_id?: (string | string) | null;
  cms_system?: CmsSystem;
  cms_link?: string | null;
  lead_system?: LeadSystem;
  lead_system_link?: string | null;
  intake_preferences: IntakePreferences;
  case_preferences?: string | null;
  commentary?: string | null;
  errors?: string | null;
  plan_to_derisk?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  sms_sender_number?: string | null;
  sms_sender_name?: string | null;
  fax?: string | null;
};
export type MatterType =
  | "Motor Vehicle Accident"
  | "Medical Malpractice"
  | "Miscellaneous"
  | "Breach of Employment Contract"
  | "Seller Non-Disclosure"
  | "Premises Liability"
  | "Broker Negligence"
  | "Property Damage"
  | "Dog Bite"
  | "Wrongful Death"
  | "Unknown";
export type MatterStatus =
  | "New Lead"
  | "Intake"
  | "Case Ingestion"
  | "Initial Review"
  | "Case Setup"
  | "Treatment"
  | "Treatment Complete"
  | "Ready for Demand"
  | "Demand Preparation"
  | "Demand Letter"
  | "Litigation"
  | "Complete"
  | "Hidden"
  | "In Progress"
  | "Client Dropped"
  | "To Be Dropped";
export type MatterSchemaForDeliverable = {
  id: string | string;
  name?: string | null;
  matter_type?: MatterType;
  status?: MatterStatus;
  date_of_loss?: string | null;
  statute_of_limitations_date?: string | null;
  city?: string | null;
  state?: string | null;
  created_at?: string | null;
  modified_at?: string | null;
  representing_attorney_id?: (string | string) | null;
  external_cms_url?: string | null;
  cms_email?: string | null;
  referral_source_description?: string | null;
};
export type AddressUpdateModel = {
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  city?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?:
    | (
        | "AF"
        | "AX"
        | "AL"
        | "DZ"
        | "AS"
        | "AD"
        | "AO"
        | "AI"
        | "AQ"
        | "AG"
        | "AR"
        | "AM"
        | "AW"
        | "AU"
        | "AT"
        | "AZ"
        | "BS"
        | "BH"
        | "BD"
        | "BB"
        | "BY"
        | "BE"
        | "BZ"
        | "BJ"
        | "BM"
        | "BT"
        | "BO"
        | "BQ"
        | "BA"
        | "BW"
        | "BV"
        | "BR"
        | "IO"
        | "BN"
        | "BG"
        | "BF"
        | "BI"
        | "CV"
        | "KH"
        | "CM"
        | "CA"
        | "KY"
        | "CF"
        | "TD"
        | "CL"
        | "CN"
        | "CX"
        | "CC"
        | "CO"
        | "KM"
        | "CG"
        | "CD"
        | "CK"
        | "CR"
        | "CI"
        | "HR"
        | "CU"
        | "CW"
        | "CY"
        | "CZ"
        | "DK"
        | "DJ"
        | "DM"
        | "DO"
        | "EC"
        | "EG"
        | "SV"
        | "GQ"
        | "ER"
        | "EE"
        | "SZ"
        | "ET"
        | "FK"
        | "FO"
        | "FJ"
        | "FI"
        | "FR"
        | "GF"
        | "PF"
        | "TF"
        | "GA"
        | "GM"
        | "GE"
        | "DE"
        | "GH"
        | "GI"
        | "GR"
        | "GL"
        | "GD"
        | "GP"
        | "GU"
        | "GT"
        | "GG"
        | "GN"
        | "GW"
        | "GY"
        | "HT"
        | "HM"
        | "VA"
        | "HN"
        | "HK"
        | "HU"
        | "IS"
        | "IN"
        | "ID"
        | "IR"
        | "IQ"
        | "IE"
        | "IM"
        | "IL"
        | "IT"
        | "JM"
        | "JP"
        | "JE"
        | "JO"
        | "KZ"
        | "KE"
        | "KI"
        | "KP"
        | "KR"
        | "KW"
        | "KG"
        | "LA"
        | "LV"
        | "LB"
        | "LS"
        | "LR"
        | "LY"
        | "LI"
        | "LT"
        | "LU"
        | "MO"
        | "MG"
        | "MW"
        | "MY"
        | "MV"
        | "ML"
        | "MT"
        | "MH"
        | "MQ"
        | "MR"
        | "MU"
        | "YT"
        | "MX"
        | "FM"
        | "MD"
        | "MC"
        | "MN"
        | "ME"
        | "MS"
        | "MA"
        | "MZ"
        | "MM"
        | "NA"
        | "NR"
        | "NP"
        | "NL"
        | "NC"
        | "NZ"
        | "NI"
        | "NE"
        | "NG"
        | "NU"
        | "NF"
        | "MK"
        | "MP"
        | "NO"
        | "OM"
        | "PK"
        | "PW"
        | "PS"
        | "PA"
        | "PG"
        | "PY"
        | "PE"
        | "PH"
        | "PN"
        | "PL"
        | "PT"
        | "PR"
        | "QA"
        | "RE"
        | "RO"
        | "RU"
        | "RW"
        | "BL"
        | "SH"
        | "KN"
        | "LC"
        | "MF"
        | "PM"
        | "VC"
        | "WS"
        | "SM"
        | "ST"
        | "SA"
        | "SN"
        | "RS"
        | "SC"
        | "SL"
        | "SG"
        | "SX"
        | "SK"
        | "SI"
        | "SB"
        | "SO"
        | "ZA"
        | "GS"
        | "SS"
        | "ES"
        | "LK"
        | "SD"
        | "SR"
        | "SJ"
        | "SE"
        | "CH"
        | "SY"
        | "TW"
        | "TJ"
        | "TZ"
        | "TH"
        | "TL"
        | "TG"
        | "TK"
        | "TO"
        | "TT"
        | "TN"
        | "TR"
        | "TM"
        | "TC"
        | "TV"
        | "UG"
        | "UA"
        | "AE"
        | "GB"
        | "US"
        | "UM"
        | "UY"
        | "UZ"
        | "VU"
        | "VE"
        | "VN"
        | "VG"
        | "VI"
        | "WF"
        | "EH"
        | "YE"
        | "ZM"
        | "ZW"
      )
    | null;
};
export type MatterClientPhoneNumberSchema = {
  phone_number: string;
};
export type MatterClientSchema = {
  id?: (string | string) | null;
  name?: string | null;
  email?: string | null;
  date_of_birth?: string | null;
  last_four_of_ssn?: string | null;
  preferred_language?: string | null;
  address?: AddressUpdateModel;
  phone_numbers?: MatterClientPhoneNumberSchema[];
};
export type ProviderStatus = "NEEDS_APPROVAL" | "APPROVED" | "REJECTED";
export type ProviderTreatmentStatus =
  | "TREATING"
  | "COMPLETED"
  | "DEVIATING"
  | "UNKNOWN"
  | "REFERRED_TO";
export type ProviderType = "ORGANIZATION" | "PRACTITIONER";
export type ProviderSearchResult = {
  /** Name of the practitioner (e.g. 'Dr. John Smith') if explicitly stated, null otherwise. */
  practitioner_name?: string | null;
  /** Common, recognizable name of the healthcare organization (e.g., 'UC San Diego Health', 'Main Street Clinic'). EXCLUDE administrative department names (e.g., 'Medical Records', 'Billing'); these belong in handles. Align with original mention's generality unless search strongly indicates a specific, distinct facility. */
  organization_name?: string | null;
  /** The address of the provider. */
  address?: string | null;
  /** The BEST (at MOST 2 handles per type) contact methods for records/billing retrieval. Do not include scheduling phone numbers, or other patient care oriented phone numbers. */
  handles: ContactHandleSchema[];
  /** A very brief (1-2 sentences) note for paralegal context. */
  internal_note?: string | null;
};
export type EncounterSchema = {
  /** The unique identifier for the encounter */
  id: string;
  /** The date of service for the encounter in ISO format (YYYY-MM-DD), if explicitly stated, null otherwise */
  date?: string | null;
  /** list of short phrases describing care provided in the encounter as mentioned in the artifact, like 'physical therapy', 'labs', 'imaging', etc. if explicitly stated, null otherwise */
  services?: string[];
  /** A very concise description of the encounter, as a one liner that might be used in a medical chronology for the case. Don't repeat the date of service in the description . Explicitly mention any ambiguities about the discovery that merit further investigation (i.e date of service not yet known, etc.) */
  description: string;
};
export type TreatmentFrequency =
  | "DAILY"
  | "WEEKLY"
  | "BI_WEEKLY"
  | "MONTHLY"
  | "AS_NEEDED"
  | "OTHER";
export type TreatmentProgress = "IMPROVING" | "STAGNANT" | "REGRESSING";
export type TreatmentCareLevel =
  | "EMERGENCY_CARE"
  | "CONSERVATIVE_CARE"
  | "DIAGNOSTIC_TESTING"
  | "SPECIALIST_CARE"
  | "SURGICAL_CARE";
export type MedicalProviderSchema = {
  id: string;
  matter_id: string;
  provider_contact_ref_id?: string | null;
  provider_service_ref_id?: string | null;
  name: string;
  status?: ProviderStatus;
  dates_of_service?: string | null;
  treatment?: string | null;
  address?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  still_treating?: string | null;
  treatment_status?: ProviderTreatmentStatus;
  treatment_status_occurred_at?: string | null;
  date_of_records_request?: string | null;
  provider_type?: ProviderType;
  practitioner_names?: string[] | null;
  description?: string | null;
  handles: ContactHandleSchema[];
  inference_reasoning?: string | null;
  fallback_leads?: ProviderSearchResult[] | null;
  encounters?: EncounterSchema[] | null;
  pain_level?: number | null;
  treatment_notes?: string | null;
  treatment_frequency?: TreatmentFrequency;
  treatment_progress?: TreatmentProgress;
  care_level?: TreatmentCareLevel;
};
export type PoliceReportAttestationStatus =
  | "rejected_verified"
  | "unclear"
  | "inferred"
  | "attested_unverified"
  | "attested_verified"
  | "verified_received";
export type PoliceReportAvailabilityStatus =
  | "verified_ready"
  | "verified_pending"
  | "unknown"
  | "available"
  | "not_available";
export type OfficerDetail = {
  name: string;
  badge_number?: string | null;
  context?: string | null;
};
export type PoliceReportEscalationReason =
  | "cant_verify_attested_report"
  | "verified_ready_but_no_automation_available"
  | "verified_but_unclear_if_ready"
  | "found_on_portal_but_failed_to_retrieve";
export type JurisdictionType =
  | "city"
  | "county"
  | "state"
  | "federal"
  | "tribal"
  | "special";
export type PoliceDepartmentEnrichmentStatus =
  | "not_enriched"
  | "enrichment_in_progress"
  | "enrichment_failed"
  | "enrichment_succeeded";
export type ServiceType =
  | "crash_reports"
  | "foia_equivalent"
  | "criminal_records"
  | "incident_reports"
  | "general_records";
export type PoliceRecordsInquiryMajorVendor =
  | "carfax_crashdocs"
  | "lexis_nexis_buycrash"
  | "independent_portal"
  | "next_request"
  | "gov_qa"
  | "not_a_portal"
  | "txdot_cris";
export type AddressSchema = {
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province_or_state?: string | null;
  postal_or_zip?: string | null;
  country_code?: string | null;
};
export type MailRequirement =
  | "CLIENT_PHOTO_ID_COPY"
  | "FIRM_LETTERHEAD"
  | "PROOF_OF_REPRESENTATION"
  | "COMPLETED_REQUEST_FORM"
  | "NOTARIZED_REQUEST_FORM";
export type FeeComponent = {
  /** The amount of the fee in cents. */
  amount_cents: number;
  /** One line description of the component of the total fee backed by research and assumptions.
    ex:
    - "Upper bound estimate of 20 pages at $0.25/page as specified by ____"
    - "Estimating up to 30min of labor at stated rate of $20/hour as specified by ____"
    - "Base certified crash report fee $8.44 as specified by ____"
    - "Added 7% for tax because site mentions it's not included in provided info" */
  description: string;
};
export type RecordsFeeEstimate = {
  /** An estimated fee for requesting a copy of a police report.
    Depending on the department, sometimes this is called a "crash report", "accident report",
    "incident report", or "police report".
    
    Rule: IF more than one report option is available, bias to the more expensive, or certified option.
    If fees are not defined for the department, fall back to county or state guidance on fees. */
  estimated_fee_cents?: number | null;
  /** All the components that led to the estimated fee */
  components?: FeeComponent[] | null;
  /** Full verbatim fee details including:
    - Exact language from official source
    - Conditions, alternatives, or special cases
    - Statutory citations (e.g., "per RSMo §610.026")
    - Payment instructions
    ALWAYS populate this when ANY fee information is available. */
  notes?: string | null;
  /** A 0-1 score indicating the confidence. */
  confidence_score?: number | null;
  /** 2-3 sentences explaining the confidence score. */
  reasoning?: string | null;
};
export type CheckMailingInstructions = {
  /** Verifiable instructions on how to request a police report from the agency by mail. */
  instructions?: string;
  /** The mailing address to send the request to. */
  address: AddressSchema;
  /** The best, single name of the entity to make the check payable to. (Payee)
    Do not place anything except for a single, bank-ready name here. */
  payable_to?: string | null;
  /** Any specific text required to be written on the check memo line, if explicitly mentioned, otherwise null.
    Example: "Police Report Request" or "Report #[REPORT_NUMBER]" or "Accident Report Fee"
    available variables: "[REPORT_NUMBER], [CLIENT_NAME], [DATE_OF_LOSS]" */
  memo_instructions?: string | null;
  /** The number of days after incident date to wait before requesting
    the records by mail, if explicitly specified, otherwise null. */
  wait_before_requesting_days?: number | null;
  /** Any department-specified, or state-specified, number of days to expect for processing, if explicitly stated, otherwise null. */
  estimated_processing_days?: number | null;
  /** Any items that are explicitly REQUIRED for requesting a police report by mail, if described, otherwise null. */
  required_items?: MailRequirement[] | null;
  /** Any special requirements for requesting a police report by mail, if described, otherwise null. */
  special_requirements?: string | null;
  /** The estimated set of fees to request a copy of a police report by mail.
    
    - (for any question of the report type being sought): assume we want the most detailed version of the report, sometimes this is "crash report", "accident report", "incident report", "police report", bias to the more expensive of the four
    - (for any page-based fee structure) assume a 20 page incident report
    - (for any requestor-based fee differences) assume legal representative of involved party
    - (for request-method based pricing) assume check + mail but bias to more expensive option
    - (for any charge for a missing report number) assume we do not have the report number
    - (for any charges related to labor) assume 30 minutes of labor
    - (for any charges related to certified copies) assume we are paying for the most certified copy */
  fee_structure?: RecordsFeeEstimate;
  /** Web pages that were used to gather the information. */
  citations?: WebSource[] | null;
};
export type DepartmentForm = {
  /** Concise title for the Form */
  title: string;
  /** Concise sentence description of the Form and its purpose. */
  description?: string | null;
  /** URL of the Form file */
  file_url: string;
};
export type ReadDepartmentRecordsChannel = {
  /** A concise title for the channel - e.g. "FOIA Portal", "Records Department Phone" */
  title?: string | null;
  /** Single sentence description of the channel and its purpose. */
  description?: string | null;
  /** Tags for record types that can be serviced by this channel.
    e.g. ["crash_reports", "foia_equivalent", "general_records"] */
  services_provided?: ServiceType[] | null;
  /** The type of channel: mail, phone, email, online_portal, fax */
  channel_type?: string | null;
  /** Web sources/citations for this channel information */
  citations?: WebSource[] | null;
  /** Concise instructions on how to request records from the police department, if explicitly mentioned, otherwise null.
    e.g. 'To request a police report, please visit the police department website and fill out the request form.' */
  instructions: string;
  handle: ContactHandleSchema;
  fee_amount?: number | null;
  major_vendor?: PoliceRecordsInquiryMajorVendor;
  /** An explicitly provided link to a pdf, docx, or other static file form that the department publishes and requires
    inquirers to fill out for making police report requests. Not every PD may ask for inquirers to provide a formal request,
    but if they do, we should capture them here - otherwise null. */
  required_document_url?: WebSource;
  mailing_instructions?: CheckMailingInstructions;
  /** Department-provided forms associated with this channel */
  forms?: DepartmentForm[] | null;
  /** Portal URL if this is an online portal channel */
  portal_url?: string | null;
  /** Portal domain if this is an online portal channel */
  portal_domain?: string | null;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department_id: string | string;
  required_document_file_url?: string | null;
};
export type ReadPoliceDepartment = {
  /** Police department name, if explicitly mentioned, otherwise null */
  name?: string | null;
  /** Police department state, if explicitly mentioned, otherwise null. e.g. 'California' */
  state_name?: string | null;
  /** Police department state code, if explicitly mentioned, otherwise null. e.g. 'CA' */
  state_code?: string | null;
  /** The best available contact methods for the police department for requesting records, documents or reports.
    - NON-EMERGENCY phone lines
    - Any specific 'records' department phone or fax numbers
    - Mailing addresses for records requests
    - Email addresses for records requests
    - Online portals for records requests
    - Department homepage */
  handles?: ContactHandleSchema[] | null;
  /** Primary source provided aliases or abbreviations for the department, e.g. 'SFPD' */
  aliases?: string[] | null;
  /** Ascertained jurisdiction type of the police department / agency, if explicitly
    stated. */
  jurisdiction_type?: JurisdictionType;
  /** Primary source provided name or description of the jurisdiction,
    e.g. 'San Francisco', 'Coffee County, Tennessee', 'Major Highways of Tennessee' */
  jurisdiction_name?: string | null;
  /** system use: Whether the police department has been enriched with additional information */
  enrichment_status?: PoliceDepartmentEnrichmentStatus;
  /** A concise, but comprehensive description of the police department, if explicitly mentioned, otherwise null.
    e.g. 'The San Francisco Police Department is the primary law enforcement agency for the City and County of San Francisco.' */
  description?: string | null;
  records_channels?: ReadDepartmentRecordsChannel[];
  /** The time zone of the police department, if explicitly mentioned, otherwise null.
    e.g. 'America/Los_Angeles' */
  time_zone?: TimeZone;
  /** Flag to mark department as do not call - prevents voice agent outreach */
  do_not_call?: boolean;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type ReadPoliceReport = {
  /** Guide to attestation statuses:
    
    <inferred>
    The involvement of this department is a system generated guess based on some circumstantial clues, like the location of the incident,
    the department hasn't been directly named anywhere in the case context. Always use this when the department is not
    directly named in the case context.
    </inferred>
    <unclear>
    The department has been named somewhere in the case context, but we have mixed indications as to whether it was involved in the incident.
    </unclear>
    <attested_unverified>
    Secondary source (client intake, staff note) attestation
    that a police report exists with this department.
    </attested_unverified>
    <rejected_verified>
    Primary source (department representative) confirmation that
    a police report does not exist within the department, e.g. 'We do not have a
    report for this incident'
    </rejected_verified>
    <attested_verified>
    Primary source (department representative, or official
    communication) confirmation that the department produced or is producing a report,
    e.g. an officer communication that says 'We are producing a report'
    </attested_verified>
    <verified_received>
    Ulimate confirmation: we have the final police report in hand
    can only be inferred from the matching incident report itself
    </verified_received> */
  attestation_status?: PoliceReportAttestationStatus;
  /** In at most 3 concise sentences, summarize our investigation into this department's involvement,
    critically addressing all three of the following points:
    
    1) How we discovered this department (source & reasoning)
    2) What actions, if any, we've taken to verify involvement
    3) Current understanding and next steps.
    
    Derivative examples:
    INFERRED status:
    "Guessed via location analysis - incident at I-405 & Sunset suggests CHP jurisdiction.
    No additional information yet. Next: call to confirm."
    
    UNCLEAR status:
    "Client mentioned 'LAPD responded' in intake (10/15). Called records dept 3x (10/20, 10/22, 10/25) - conflicting info.
    Officer Smith said 'checking their system and to call back in a few days'.
    Next: Request supervisor callback for definitive answer."
    
    ATTESTED_UNVERIFIED status:
    "Client's intake form states 'SFPD took report at scene, got card from Officer Chen #4521'.
    Found matching Officer Chen in SFPD roster. Called records (11/1) - hold times excessive.
    Next: Submit online records request with officer badge number."
    
    REJECTED_VERIFIED status:
    "Initially suspected due to jurisdiction overlap. Spoke with Records Supervisor Johnson (11/5, recorded).
    Confirmed: 'We did not respond to that location on [date]. Try County Sheriff.'
    Investigation complete for this department. Consider marking inactive."
    
    ATTESTED_VERIFIED status:
    "Department confirmed involvement via email from Records Dept (11/10): 'Report #2024-1234 is being processed.'
    Processing fee paid ($25). Estimated delivery: 7-10 business days.
    Next: Follow up on 11/20 if not received."
    
    VERIFIED_RECEIVED status:
    "Full report was found in case files. 8 pages, includes witness statements and diagram.
    Officer Rodriguez #7823 was primary. Report confirms client's account of events.
    Investigation complete. All documentation uploaded to case file." */
  attestation_description?: string | null;
  /** Attested availability status of the police report, only when explicitly mentioned by a primary source, otherwise null.
    <verified_ready>
    The police department has confirmed that the report is available.
    </verified_ready>
    <verified_pending>
    The police department has confirmed they are responsible for the report, but that the report is not available yet, but will be on some timeline.
    </verified_pending>
    <unknown>
    No explicit mention yet of whether the police department is responsible for the report, or if we have any other information to go on.
    </unknown> */
  availability_status?: PoliceReportAvailabilityStatus;
  /** When availability status is pending, an estimated date for when the police report might be ready, only when explicitly mentioned by a primary source, otherwise null.
    e.g. 'The report is expected to be ready by 11/20' */
  availability_estimate?: string | null;
  /** A concise, but comprehensive description of the police involvement, if explicitly mentioned, otherwise null.
    e.g. 'The police department responded to the incident, took statements from witnesses, spoke to both drivers, etc.' */
  description?: string | null;
  /** Department-issued identifier for the report
    e.g. police report number, TCR number, accident report number, etc. */
  report_identifier?: string | null;
  /** The date of the incident noted on the report, if explicitly defined by a primary source,
    otherwise null. */
  reported_incident_date?: string | null;
  /** Location of the incident, if explicitly mentioned, otherwise null.
    e.g. '123 Main St, San Francisco, CA 94133' */
  reported_location?: string | null;
  /** Officers explicitly mentioned in the text, if explicitly mentioned, otherwise null
    e.g. 'Officer John Doe, Officer Jane Smith, etc.' */
  involved_officers?: OfficerDetail[] | null;
  /** Internal ID of the associated police department - not set during extraction */
  police_department_id?: (string | string) | null;
  /** [system use] The reason this report has been escalated to a human. */
  escalation_reason?: PoliceReportEscalationReason;
  matter_id: string | string;
  id: string | string;
  created_at: string;
  modified_at: string;
  police_department: ReadPoliceDepartment;
};
export type TreatmentRecordRequestActor = "user" | "agent";
export type TreatmentRecordTypes =
  | "Bills"
  | "Records"
  | "Facility Records"
  | "Facility Bills"
  | "Radiology"
  | "ER Physician Bills"
  | "Other"
  | "Unknown";
export type TreatmentRecordRequestStatus =
  | "not_ready"
  | "pending"
  | "request_submitted"
  | "request_received"
  | "awaiting_invoice"
  | "pending_payment"
  | "records_pending"
  | "records_delivered"
  | "records_ingested"
  | "rejected"
  | "cancelled"
  | "auto_retried";
export type Blockers =
  | "HIPAA_PACKAGE_NOT_READY"
  | "CLIENT_INFORMATION_NOT_ENTERED"
  | "PROVIDER_CONTACT_INFORMATION_NOT_ENTERED"
  | "PORTAL_REQUEST_ID_REQUIRED"
  | "NO_REQUEST_SENT_AT"
  | "VOICE_AI_INFORMATION_NOT_ENTERED"
  | "FOLLOWUP_HIPAA_PACKAGE_NOT_SUBMITTED"
  | "FOLLOWUP_CHANNEL_NOT_CONFIGURED"
  | "FOLLOWUP_CHANNEL_UNSUPPORTED"
  | "FOLLOWUP_FAX_NUMBER_MISSING"
  | "FOLLOWUP_EMAIL_ADDRESS_MISSING";
export type TreatmentRecordRequestAutomationBlockedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "blocked";
  blocked_by?: Blockers[];
};
export type TreatmentRecordRequestAutomationFailedSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "failed";
};
export type TreatmentRecordRequestAutomationIdleSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "idle";
};
export type TreatmentRecordRequestAutomationRunningSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "running";
};
export type TreatmentRecordRequestAutomationSleepingSchema = {
  details?: string | null;
  checked_status_at?: string | null;
  status?: "sleeping";
  sleep_until: string;
};
export type PatientInformation = {
  client_name: string;
  client_date_of_birth: string;
};
export type TreatmentRecordRequestChannelType =
  | "portal"
  | "human"
  | "email"
  | "fax"
  | "mail"
  | "phone";
export type RequestDetails = {
  request_method: TreatmentRecordRequestChannelType;
  request_sent_at: string;
  request_method_target: string;
  contact_number: string;
};
export type RequesteeInformation = {
  requestee_name: string;
};
export type VoiceAiCallArgs = {
  patient_information: PatientInformation;
  request_details: RequestDetails;
  requestee_information: RequesteeInformation;
  /** Human-readable description of the requested records (e.g., Medical Records, Bills, Radiology). */
  request_type_label?: string | null;
  /** Skip business-hours scheduling and execute the call immediately. Used for manual/bulk calls that should run ASAP. */
  skip_scheduling_delay?: boolean;
};
export type AlternateContactHandleType = "fax" | "email" | "mailing_address";
export type AlternateContactSchema = {
  /** Type of contact method (fax or mailing address) */
  handle_type: AlternateContactHandleType;
  /** The actual contact value (fax number or mailing address) */
  value: string;
  /** Optional human-readable label or description */
  label?: string | null;
  /** Optional confidence score from AI extraction (0-1) */
  confidence?: number | null;
};
export type ContactDiscoveryCallSummarySchema = {
  /** Short text describing what the facility told us. */
  call_summary: string;
  /** List of submission channels (portal, fax, email) mentioned during the call. */
  submission_methods?: string[];
  /** Documents or prerequisites (HIPAA, ID, invoices) called out by the facility. */
  required_documents?: string[];
  /** Structured alternates (fax numbers, mailing addresses) discovered during the call. */
  alternate_contacts?: AlternateContactSchema[];
  /** Optional free-form notes for additional context. */
  notes?: string | null;
};
export type TransmissionStrategyConfig = {
  enabled?: boolean;
  channel?: TreatmentRecordRequestChannelType;
  interval_days?: number;
  max_attempts?: number;
  stop_on_voice_ai_contact?: boolean;
  stop_on_status_change?: boolean;
  last_transmission_at?: string | null;
  next_transmission_at?: string | null;
  total_transmissions_sent?: number;
  failed_attempts?: number;
};
export type TreatmentRecordRequestMetadataSchema = {
  slack_message_id?: string | null;
  disable_automations?: boolean;
  automation_state_v2?:
    | (
        | ({
            status: "blocked";
          } & TreatmentRecordRequestAutomationBlockedSchema)
        | ({
            status: "failed";
          } & TreatmentRecordRequestAutomationFailedSchema)
        | ({
            status: "idle";
          } & TreatmentRecordRequestAutomationIdleSchema)
        | ({
            status: "running";
          } & TreatmentRecordRequestAutomationRunningSchema)
        | ({
            status: "sleeping";
          } & TreatmentRecordRequestAutomationSleepingSchema)
      )
    | null;
  ad_hoc_voice_ai_args?: VoiceAiCallArgs;
  front_conversation_id?: string | null;
  front_conversation_url?: string | null;
  email_sent_at?: string | null;
  /** Number of days between voice AI follow-up calls */
  voice_ai_check_interval_days?: number;
  next_scheduled_call?: string | null;
  request_submitted_at?: string | null;
  initial_contact_method_call_completed_at?: string | null;
  voice_ai_call_summary?: string | null;
  voice_ai_discovered_alternates?: {
    [key: string]: any;
  }[];
  voice_ai_call_id?: string | null;
  superseded_by_request_id?: string | null;
  supersession_reason?: string | null;
  last_notification_category?: string | null;
  last_notification_subcategory?: string | null;
  last_call_key_information?: {
    [key: string]: any;
  };
  last_voice_ai_call_at?: string | null;
  last_voice_ai_call_summary?: string | null;
  dynamic_retry_delay_hours?: number | null;
  contact_discovery_summary?: ContactDiscoveryCallSummarySchema;
  contact_discovery_last_updated_at?: string | null;
  hipaa_package_locked?: boolean;
  hipaa_package_locked_at?: string | null;
  hipaa_package_submission_channel?: TreatmentRecordRequestChannelType;
  hipaa_package_submission_workflow_id?: string | null;
  alternate_contact?: AlternateContactSchema;
  forked_from_request_id?: string | null;
  transmission_strategy?: TransmissionStrategyConfig;
};
export type TreatmentRecordRequestPortalType =
  | "smart_request"
  | "mro"
  | "chartswap";
export type PortalRecordType = {
  id: string | string;
  created_at: string;
  modified_at: string;
  original_name?: string | null;
  record_type: TreatmentRecordTypes;
};
export type PortalProvider = {
  id: string | string;
  created_at: string;
  modified_at: string;
  name: string;
  portal: TreatmentRecordRequestPortalType;
  active?: boolean;
  external_id?: string | null;
  site?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  fax?: string | null;
  address_line_1?: string | null;
  address_line_2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  record_types?: PortalRecordType[] | null;
};
export type TreatmentRecordRequestChannelSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  type: TreatmentRecordRequestChannelType;
  portal_provider_id?: (string | string) | null;
  phone_number?: string | null;
  address?: string | null;
  email?: string | null;
  fax?: string | null;
  instructions?: string | null;
  portal_record_request_id?: string | null;
  provider_contact_unit_handle_id?: (string | string) | null;
  provider_directory_contact_point_id?: (string | string) | null;
  portal_provider?: PortalProvider;
};
export type FaxTransmissionStatusSchema = {
  id?: (string | string) | null;
  status?: ("pending" | "sent" | "delivered" | "failed") | null;
  sent_at?: string | null;
  delivered_at?: string | null;
  failed_at?: string | null;
  transmission_id?: string | null;
  error_message?: string | null;
};
export type EmailTransmissionStatusSchema = {
  sent_at?: string | null;
  front_conversation_id?: string | null;
  front_conversation_url?: string | null;
  recipient_email?: string | null;
};
export type TreatmentRecordRequestSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  provider_id: string | string;
  actor?: TreatmentRecordRequestActor;
  is_certified?: boolean;
  is_urgent?: boolean;
  provided_records?: TreatmentRecordTypes[];
  status?: TreatmentRecordRequestStatus;
  metadata?: TreatmentRecordRequestMetadataSchema;
  channel?: TreatmentRecordRequestChannelSchema;
  fax_transmission_status?: FaxTransmissionStatusSchema;
  email_transmission_status?: EmailTransmissionStatusSchema;
  task_id?: (string | string) | null;
};
export type TaskSchemaForDeliverable = {
  task: TaskWithUpdatesSchema;
  firm: FirmSchema;
  matter: MatterSchemaForDeliverable;
  client: MatterClientSchema;
  coverage?: CoverageSchema;
  provider?: MedicalProviderSchema;
  assignee?: CustomUserSchema;
  police_report?: ReadPoliceReport;
  treatment_record_request?: TreatmentRecordRequestSchema;
};
export type DeliverableAgentContext = {
  /** Single sentence (≤20 words) explaining why this deliverable was created. */
  rationale?: string | null;
};
export type DeliverableMatchStatus = "Needs Review" | "Approved" | "Rejected";
export type DeliverableMatchContext = {
  /** ONE concise sentence explaining the match. Example: 'Document contains signed retainer dated 4/15/2024.' */
  rationale?: string | null;
  /** Up to 3 short, direct quotes supporting the match. Keep each quote under 20 words. */
  key_evidence?: string[] | null;
};
export type DeliverableMatchSchema = {
  status?: DeliverableMatchStatus;
  confidence?: number | null;
  context?: DeliverableMatchContext;
  id: string | string;
  deliverable_id: string | string;
  artifact?: DetailedArtifactSchema;
  front_conversation_id?: (string | string) | null;
  created_at: string;
  modified_at: string;
  user_id?: (string | string) | null;
  user_name?: string | null;
  agent?: AgentType;
};
export type DeliverableMatchSchemaRead = {
  status?: DeliverableMatchStatus;
  confidence?: number | null;
  context?: DeliverableMatchContext;
  id: string | string;
  deliverable_id: string | string;
  artifact?: DetailedArtifactSchema;
  front_conversation_id?: (string | string) | null;
  created_at: string;
  modified_at: string;
  user_id?: (string | string) | null;
  user_name?: string | null;
  agent?: AgentType;
  /** Title of the artifact if match is to an artifact. */
  artifact_id: (string | string) | null;
};
export type DeliverableSchema = {
  name: string;
  prompt: string;
  artifact_criteria?: DeliverableArtifactCriteria;
  anchors?: DeliverableAnchors;
  status?: DeliverableStatus;
  id: string | string;
  task: TaskSchemaForDeliverable;
  created_at: string;
  modified_at: string;
  user_id?: (string | string) | null;
  agent?: AgentType;
  agent_context?: DeliverableAgentContext;
  matches?: DeliverableMatchSchema[];
  /** Canonical group of the task (backwards compatibility). */
  task_canonical_group: TaskGroups;
  /** Canonical type of the task (backwards compatibility). */
  task_canonical_type: TaskType;
};
export type DeliverableSchemaRead = {
  name: string;
  prompt: string;
  artifact_criteria?: DeliverableArtifactCriteria;
  anchors?: DeliverableAnchors;
  status?: DeliverableStatus;
  id: string | string;
  task: TaskSchemaForDeliverable;
  created_at: string;
  modified_at: string;
  user_id?: (string | string) | null;
  agent?: AgentType;
  agent_context?: DeliverableAgentContext;
  matches?: DeliverableMatchSchemaRead[];
  /** ID of the task (backwards compatibility). */
  task_id: string | string;
  /** Name of the task (backwards compatibility). */
  task_name: string;
  /** Canonical group of the task (backwards compatibility). */
  task_canonical_group: TaskGroups;
  /** Canonical type of the task (backwards compatibility). */
  task_canonical_type: TaskType;
  /** Number of matches needing review. */
  pending_matches: number;
  /** Number of rejected matches. */
  rejected_matches: number;
  /** Whether completing the parent task requires evidence for this deliverable. */
  requires_evidence?: boolean;
};
export type DeliverableCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchema;
  task_id: string | string;
  name: string;
  prompt: string;
};
export type DeliverableCreatedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchemaRead;
  task_id: string | string;
  name: string;
  prompt: string;
};
export type DiscriminatedDeliverableCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_created";
  payload: DeliverableCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DiscriminatedDeliverableCreatedEventRead = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_created";
  payload: DeliverableCreatedEventRead;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DeliverableStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchema;
  old_status: DeliverableStatus;
  new_status: DeliverableStatus;
};
export type DeliverableStatusChangedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchemaRead;
  old_status: DeliverableStatus;
  new_status: DeliverableStatus;
};
export type DiscriminatedDeliverableStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_status_changed";
  payload: DeliverableStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DiscriminatedDeliverableStatusChangedEventRead = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_status_changed";
  payload: DeliverableStatusChangedEventRead;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DeliverableSatisfiedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchema;
  task_id: string | string;
  satisfied_by_match_id: string | string;
};
export type DeliverableSatisfiedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable: DeliverableSchemaRead;
  task_id: string | string;
  satisfied_by_match_id: string | string;
};
export type DiscriminatedDeliverableSatisfiedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_satisfied";
  payload: DeliverableSatisfiedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DiscriminatedDeliverableSatisfiedEventRead = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_satisfied";
  payload: DeliverableSatisfiedEventRead;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DeliverableMatchCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchema;
  deliverable: DeliverableSchema;
  artifact_id: string | string;
  confidence: number | null;
};
export type DeliverableMatchCreatedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchemaRead;
  deliverable: DeliverableSchemaRead;
  artifact_id: string | string;
  confidence: number | null;
};
export type DiscriminatedDeliverableMatchCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_match_created";
  payload: DeliverableMatchCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DiscriminatedDeliverableMatchCreatedEventRead = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_match_created";
  payload: DeliverableMatchCreatedEventRead;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DeliverableMatchStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchema;
  deliverable_id: string | string;
  artifact_id: string | string;
  old_status: DeliverableMatchStatus;
  new_status: DeliverableMatchStatus;
};
export type DeliverableMatchStatusChangedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchemaRead;
  deliverable_id: string | string;
  artifact_id: string | string;
  old_status: DeliverableMatchStatus;
  new_status: DeliverableMatchStatus;
};
export type DiscriminatedDeliverableMatchStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_match_status_changed";
  payload: DeliverableMatchStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DiscriminatedDeliverableMatchStatusChangedEventRead = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_match_status_changed";
  payload: DeliverableMatchStatusChangedEventRead;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DeliverableMatchApprovedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchema;
  deliverable_id: string | string;
  artifact_id: string | string;
};
export type DeliverableMatchApprovedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  deliverable_match: DeliverableMatchSchemaRead;
  deliverable_id: string | string;
  artifact_id: string | string;
};
export type DiscriminatedDeliverableMatchApprovedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_match_approved";
  payload: DeliverableMatchApprovedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DiscriminatedDeliverableMatchApprovedEventRead = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "deliverable_match_approved";
  payload: DeliverableMatchApprovedEventRead;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DocumentTemplateSchema = {
  id: string | string;
  name: string;
  template_type: string;
  requires_signatures: boolean;
  client_signature_required?: boolean;
  attorney_signature_required?: boolean;
  attorney_id?: (string | string) | null;
};
export type DocumentInstanceSchema = {
  id: string | string;
  status: string;
  created_at?: string | null;
  document_template: DocumentTemplateSchema;
  cached_pdf_url?: string | null;
  field_values_source?: string | null;
};
export type DocumentCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: (string | string) | null;
  document: DocumentInstanceSchema;
};
export type DiscriminatedDocumentCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "document_created";
  payload: DocumentCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DocumentInstanceStatus =
  | "CREATED"
  | "SENT"
  | "COMPLETED"
  | "VOIDED"
  | "SIGNED";
export type DocumentStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: (string | string) | null;
  document: DocumentInstanceSchema;
  old_status?: DocumentInstanceStatus;
  new_status?: DocumentInstanceStatus;
};
export type DiscriminatedDocumentStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "document_status_changed";
  payload: DocumentStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DocumentDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: (string | string) | null;
  document: DocumentInstanceSchema;
};
export type DiscriminatedDocumentDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "document_deleted";
  payload: DocumentDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoRGenerationAuditEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id: string | string;
  template_id: string | string;
  coverage_id?: (string | string) | null;
  defaults: {
    [key: string]: any;
  }[];
  submitted_merge_fields: {
    [key: string]: any;
  };
  diff: {
    [key: string]: any;
  };
};
export type DiscriminatedLoRGenerationAuditEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "document_lor_generation_audit";
  payload: LoRGenerationAuditEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type HipaaGenerationAuditEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id: string | string;
  template_id: string | string;
  request_id?: (string | string) | null;
  defaults: {
    [key: string]: any;
  }[];
  submitted_merge_fields: {
    [key: string]: any;
  };
  diff: {
    [key: string]: any;
  };
};
export type DiscriminatedHipaaGenerationAuditEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "document_hipaa_package_generation_audit";
  payload: HipaaGenerationAuditEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoRDataUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  document_id?: (string | string) | null;
  coverage_id?: (string | string) | null;
  mapping?: string | null;
  old_value?: string | null;
  new_value?: string | null;
};
export type DiscriminatedLoRDataUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "lor_data_updated";
  payload: LoRDataUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type IncidentMentionStubSchema = {
  id: string | string;
  created_at: string;
  artifact_id: string | string;
  artifact_type: ArtifactType;
  citation_ids?: string[] | null;
  confidence?: number | null;
  context?: string | null;
  reasoning?: string | null;
};
export type LocationNormalizationStatus =
  | "pending"
  | "success"
  | "failed"
  | "low_confidence";
export type IncidentLocationSchema = {
  /** Original location from extraction */
  raw_location?: string | null;
  /** Google Maps Place ID */
  place_id?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  formatted_address?: string | null;
  short_formatted_address?: string | null;
  display_name?: string | null;
  street_number?: string | null;
  route?: string | null;
  city?: string | null;
  county?: string | null;
  state?: string | null;
  state_long?: string | null;
  postal_code?: string | null;
  country?: string | null;
  country_long?: string | null;
  plus_code_global?: string | null;
  plus_code_compound?: string | null;
  primary_type?: string | null;
  types?: string[];
  google_maps_uri?: string | null;
  normalization_confidence?: number | null;
  confidence_reasoning?: string | null;
  normalization_status?: LocationNormalizationStatus;
  satellite_image_url?: string | null;
  /** Street View facing north (0°) */
  streetview_image_url_0?: string | null;
  /** Street View facing east (90°) */
  streetview_image_url_90?: string | null;
  /** Street View facing south (180°) */
  streetview_image_url_180?: string | null;
  /** Street View facing west (270°) */
  streetview_image_url_270?: string | null;
};
export type IncidentSchema = {
  /** Concise, 1-2 sentence summary of the facts of the incident, limited to events that occurred on the date of loss. Involved parties, location, and nature of the incident should be included. */
  description?: string | null;
  date_of_loss?: string | null;
  /** Time of day when the incident occurred, helping establish circumstances and potential contributing factors. */
  time_of_loss?: string | null;
  /** Where the incident took place, formatted for geocoding.
    
    Police reports typically include location in standardized formats - preserve these exactly:
    - Street addresses: "1234 OAK ST" or "1234 Oak Street"
    - Intersections: "MAIN ST / 5TH AVE" or "Main St at 5th Ave"
    - Highway locations: "I-95 NB MM 42.3" or "I-95 Northbound mile marker 42"
    - Relative positions: "100 FT N OF MAIN ST" or "100 feet north of Main St"
    - Reference points: "IFO 123 MAIN ST" (in front of) or "in front of 123 Main St"
    
    When extracting from police reports, look for fields like:
    - "Location of Occurrence", "Crash Location", "Scene Location"
    - "Street/Highway", "At Intersection Of", "Nearest Cross Street"
    - "Feet/Direction From", "Reference Point"
    
    Output format: "[Address/Intersection], [City], [State] [ZIP if available]"
    
    Examples:
    - "1234 Oak Street, Springfield, IL 62701"
    - "Main Street and 5th Avenue, Newark, NJ"
    - "I-95 Northbound mile marker 42, Philadelphia, PA"
    - "Route 1, 100 feet south of Oak Avenue, Edison, NJ"
    - "123 Main St (in front of Walmart), Anytown, NJ"
    
    Include city and state when available. Do not include narrative text. */
  location?: string | null;
  /** Latitude coordinate if explicitly provided in the source (e.g., from a police report or GPS data). Only extract if exact coordinates are stated, do not estimate. */
  extracted_latitude?: number | null;
  /** Longitude coordinate if explicitly provided in the source (e.g., from a police report or GPS data). Only extract if exact coordinates are stated, do not estimate. */
  extracted_longitude?: number | null;
  /** Cross street at incident location, if explicitly stated, otherwise null. */
  cross_street?: string | null;
  /** Environmental conditions at the time of incident that may have contributed to or affected the circumstances. */
  weather_conditions?: string | null;
  /** Factual description of injuries sustained, important for damages assessment and medical records analysis. */
  injuries_described?: string | null;
  /** Category or nature of the incident (vehicle collision, slip and fall, workplace injury, etc.) for case classification and strategy. */
  incident_type?: string | null;
  /** Whether emergency medical care was sought immediately following the incident - relevant for damages and treatment documentation. */
  hospital?: string | null;
  /** Whether emergency medical services responded to the scene - indicates severity and creates potential documentation trail. */
  ambulance?: string | null;
  /** Whether law enforcement documented the incident - critical for obtaining official reports and witness information. */
  police_report?: string | null;
  /** Ongoing medical care status - important for understanding continuing damages and treatment coordination. */
  current_treatment?: string | null;
  /** Presence of witnesses to the incident - essential for case investigation and potential testimony. */
  witnesses?: string | null;
  /** Whether witness statements were obtained - important for preserving testimony and case preparation. */
  statements?: string | null;
  /** Whether photographic or video evidence was captured - crucial for case documentation and demonstrative evidence. */
  photos?: string | null;
  /** Any prior legal representation for this incident - important for conflicts analysis and case coordination. */
  representation?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  /**
            List of mentions of this incident in the matter, if any.
             */
  mentions?: IncidentMentionStubSchema[] | null;
  /** Normalized location data from Google Maps */
  normalized_location?: IncidentLocationSchema;
};
export type IncidentCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  incident: IncidentSchema;
};
export type DiscriminatedIncidentCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "incident_created";
  payload: IncidentCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type IncidentUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  incident: IncidentSchema;
  diff: {
    [key: string]: any;
  };
  user_id?: (string | string) | null;
};
export type DiscriminatedIncidentUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "incident_updated";
  payload: IncidentUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type IntakeNoteEntrySchema = {
  at?: string;
  text?: string;
  author_id?: (string | string) | null;
  author_name?: string;
};
export type EmergencyContactSchema = {
  name?: string;
  phone?: string;
  relationship?: string;
};
export type PersonalInfoSchema = {
  full_name?: string;
  dob?: string;
  ssn_last_4?: string;
  phone?: string;
  email?: string;
  address?: string;
  preferred_contact?: string;
  best_time_to_reach?: string;
  drivers_license_number?: string;
  drivers_license_state?: string;
  preferred_language?: "English" | "Spanish" | "";
  emergency_contact?: EmergencyContactSchema;
};
export type IncidentInfoSchema = {
  date?: string;
  time?: string;
  location?: string;
  state?: string;
  weather?: string;
  from_to?: string;
  type?: string;
  description?: string;
  present_complaints?: string;
  ems?: boolean;
  hospital?: string;
  currently_treating?: boolean;
  police_report?: boolean;
  police_dept?: string;
  photos?: boolean;
  witnesses?: string;
  police_report_number?: string;
};
export type DefendantSchema = {
  name?: string;
  address?: string;
  drivers_license_number?: string;
  drivers_license_state?: string;
  drivers_license_plate?: string;
  insurance_company_name?: string;
  policy_holder_name?: string;
  policy_number?: string;
  claim_number?: string;
  make_model_year?: string;
  carrier_v2_id?: (string | string) | null;
  carrier_division_id?: (string | string) | null;
};
export type PipInsuranceSchema = {
  company_name?: string;
  policy_holder_name?: string;
  policy_number?: string;
  claim_number?: string;
  make_model_year?: string;
  license_plate?: string;
  carrier_v2_id?: (string | string) | null;
  carrier_division_id?: (string | string) | null;
};
export type InsuranceSchema = {
  defendants?: DefendantSchema[];
  pip_insurance?: PipInsuranceSchema;
  health_provider?: string;
  health_type?: string;
  health_member_id?: string;
  health_group_id?: string;
  carrier_v2_id?: (string | string) | null;
  carrier_division_id?: (string | string) | null;
};
export type TreatmentSchema = {
  provider?: string;
  date_of_service?: string;
  treatment_received?: string;
  still_treating?: boolean;
};
export type MedicalWageHistorySchema = {
  treatments?: TreatmentSchema[];
  has_lost_wages?: boolean;
  employer?: string;
  job_title?: string;
  hourly_wage?: string;
  employment_duration?: string;
  prior_medical_history?: string;
};
export type LegalLifestyleSchema = {
  prior_legal_history?: string;
  lifestyle_impact?: string;
  pending_bills?: string;
  out_of_pocket_expenses?: string;
  other_notes?: string;
};
export type ReferralSourceSchema = {
  description?: string;
};
export type PremisesAddressSchema = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
};
export type PremisesInfoSchema = {
  property_name?: string;
  property_type?: string;
  address?: PremisesAddressSchema;
  location_description?: string;
  parcel_id?: string;
  legal_description?: string;
  owner_name?: string;
  owner_phone?: string;
  owner_email?: string;
  owner_address?: string;
  management_company?: string;
  management_phone?: string;
  management_email?: string;
  hazard_description?: string;
  hazard_reported_to?: string;
  prior_complaints?: string;
};
export type EvidenceStatusValue =
  | "CONFIRMED"
  | "EXISTS_NOT_HAVE"
  | "DOES_NOT_EXIST"
  | "UNCERTAIN";
export type EvidenceStatusSchema = {
  status?: EvidenceStatusValue;
  notes?: string;
};
export type EvidenceStatusesSchema = {
  photos?: EvidenceStatusSchema;
  witnesses?: EvidenceStatusSchema;
  police_report?: EvidenceStatusSchema;
  ems?: EvidenceStatusSchema;
  medical_treatment?: EvidenceStatusSchema;
  first_party?: EvidenceStatusSchema;
  health?: EvidenceStatusSchema;
  third_party?: EvidenceStatusSchema;
};
export type CompanionRowSchema = {
  client_name: string;
  details?: string | null;
  created_intake_id?: (string | string) | null;
};
export type ScreeningDataSchema = {
  answers?: {
    [key: string]: string;
  };
  text_fields?: {
    [key: string]: {
      [key: string]: string;
    };
  };
  notes?: string;
  in_progress?: boolean;
  intaker_id?: (string | string) | null;
  intaker_name?: string;
};
export type QualificationAnswerEnum = "yes" | "no" | "unknown";
export type QualificationOutcomeEnum =
  | "qualified"
  | "not_qualified"
  | "pending";
export type DataCollectionAnswerSchema = {
  value?: string | null;
  asked?: boolean;
  arrayValue?:
    | {
        [key: string]: string;
      }[]
    | null;
};
export type TranscriptStatusEnum =
  | "waiting"
  | "processing"
  | "complete"
  | "no_call_found"
  | "failed";
export type AiExtractedFieldStatusEnum =
  | "extracted"
  | "confirmed"
  | "edited"
  | "rejected";
export type AiExtractedFieldSchema = {
  value?: string;
  confidence?: number;
  source_quote?: string | null;
  status?: AiExtractedFieldStatusEnum;
};
export type ScreeningV2DataSchema = {
  version?: "v2";
  current_screen?: number;
  /** Question ID -> yes/no/unknown answer */
  qualification_answers?: {
    [key: string]: QualificationAnswerEnum;
  };
  /** Question ID -> whether context prompt was confirmed */
  qualification_context_asked?: {
    [key: string]: boolean;
  };
  /** Question ID -> intaker's note explaining the answer */
  qualification_notes?: {
    [key: string]: string;
  };
  qualification_outcome?: QualificationOutcomeEnum;
  qualification_completed_at?: string | null;
  retainer_sent_at?: string | null;
  rejected_at?: string | null;
  escalated_at?: string | null;
  /** Question ID -> answer data with value and asked status */
  data_collection_answers?: {
    [key: string]: DataCollectionAnswerSchema;
  };
  /** Legacy - List of field IDs that were asked during the call */
  collected_fields?: string[];
  collection_completed_at?: string | null;
  transcript_status?: TranscriptStatusEnum;
  matched_call_id?: string | null;
  /** Field ID -> AI-extracted data with confidence and source */
  ai_extracted_fields?: {
    [key: string]: AiExtractedFieldSchema;
  };
  /** Field ID -> human-confirmed or edited value */
  human_reviewed_fields?: {
    [key: string]: string;
  };
  review_completed_at?: string | null;
  intaker_id?: (string | string) | null;
  intaker_name?: string;
};
export type IntakeFormSchema = {
  loe_status?: string;
  attorney_decision?: string;
  rejection_reason?: string;
  hipaa_status?: string;
  declaration_status?: string;
  insurance_status?: string;
  general_notes?: string;
  general_notes_log?: IntakeNoteEntrySchema[];
  personal_info?: PersonalInfoSchema;
  incident_info?: IncidentInfoSchema;
  insurance?: InsuranceSchema;
  medical_wage_history?: MedicalWageHistorySchema;
  legal_lifestyle?: LegalLifestyleSchema;
  referral_source?: ReferralSourceSchema;
  premises_info?: PremisesInfoSchema;
  lead_source?: string;
  schema_version?: string;
  evidence_statuses?: EvidenceStatusesSchema;
  companions?: CompanionRowSchema[];
  screening?: ScreeningDataSchema;
  screening_v2?: ScreeningV2DataSchema;
  provider_service_ids?: (string | string)[];
};
export type IntakeSummarySchema = {
  /** Positive aspects of the case */
  green_flags: string;
  /** Caution areas requiring attention */
  yellow_flags: string;
  /** Major concerns or red flags */
  red_flags: string;
  /** Brief case overview */
  case_summary: string;
  /** Single bucket recommendation: Likely Accept | Potential Accept | Uncertain | Potential Decline | Likely Decline */
  recommendation_decision: string;
  /** Two-sentence reasoning highlighting decisive liability, treatment, and coverage factors */
  recommendation_reason: string;
  /** Heading to prefix the counterpoint, including trailing colon. If recommendation_decision contains 'Accept', set to 'Why you might not take the case:'. If it contains 'Decline', set to 'Why you would take the case:'. If it contains 'Uncertain', set to null. */
  recommendation_counterpoint_heading?: string | null;
  /** Single-sentence strongest opposing viewpoint to the recommendation. If recommendation_decision contains 'Accept', give the best reason to not take the case. If it contains 'Decline', give the best reason to take the case. If it contains 'Uncertain', set to null. */
  recommendation_counterpoint?: string | null;
};
export type IntakeSchema = {
  id: string | string;
  firm: string | string;
  form_data: IntakeFormSchema;
  summary?: IntakeSummarySchema;
  created_at: string;
  modified_at: string;
  creator?: (string | string) | null;
  assigned_to?: (string | string) | null;
  companion_root?: (string | string) | null;
  matter_id?: (string | string) | null;
  companion_count?: number;
  is_companion_primary?: boolean;
};
export type IntakeCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  intake: IntakeSchema;
};
export type DiscriminatedIntakeCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "intake_created";
  payload: IntakeCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type IntakeUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  intake: IntakeSchema;
  diff: {
    [key: string]: any;
  };
  previous?: IntakeSchema;
};
export type DiscriminatedIntakeUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "intake_updated";
  payload: IntakeUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type IntakeStatusEnum =
  | "in_progress"
  | "sent_for_signature"
  | "signed"
  | "archived"
  | "rejected"
  | "chase"
  | "lost"
  | "escalated";
export type IntakeStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  intake: IntakeSchema;
  old_status: IntakeStatusEnum;
  new_status: IntakeStatusEnum;
};
export type DiscriminatedIntakeStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "intake_status_changed";
  payload: IntakeStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ArtifactSchema = {
  id: string | string;
  matter_id: string | string;
  name: string;
  artifact_type: ArtifactType;
  created_at: string;
  modified_at: string;
};
export type MarkdownTransformCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  artifact: ArtifactSchema;
  artifact_id: string | string;
  blob_id: string | string;
};
export type DiscriminatedMarkdownTransformCompletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "blob_transform_completed";
  payload: MarkdownTransformCompletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterClientCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter_client: MatterClientSchema;
};
export type DiscriminatedMatterClientCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_client_created";
  payload: MatterClientCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterClientUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter_client: MatterClientSchema;
  diff: {
    [key: string]: any;
  };
  previous: MatterClientSchema;
};
export type DiscriminatedMatterClientUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_client_updated";
  payload: MatterClientUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterClientPhoneNumberAddedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter_client: MatterClientSchema;
  phone_number: string;
};
export type DiscriminatedMatterClientPhoneNumberAddedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_client_phone_number_added";
  payload: MatterClientPhoneNumberAddedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type AltAttachmentSchema = {
  id: string;
  name?: string | null;
  content_type?: string | null;
  size?: number | null;
};
export type MessageIdAndAttachmentSchema = {
  message_id: string;
  attachment: AltAttachmentSchema;
};
export type MatterServices =
  | "Screening & Retainer"
  | "Intake Call"
  | "Case Setup"
  | "Medical Records"
  | "Demand";
export type SimplifiedBillingCode =
  | "records_retrieval"
  | "demand_only"
  | "property_damage"
  | "in_flight"
  | "back_office"
  | "full_pre_lit"
  | "case_setup_only"
  | "litigation_only";
export type TaskConfigSchema = {
  gate_tasks_during_initial_review?: boolean;
};
export type MatterSchema = {
  firm: string | string;
  name?: string | null;
  type?: string | null;
  creator?: (string | string) | null;
  case_manager_id?: (string | string) | null;
  auto_task_assignment?: boolean | null;
  matter_type?: MatterType;
  status?: MatterStatus;
  clients?: MatterClientSchema[];
  front_attachments?: MessageIdAndAttachmentSchema[];
  front_attachment_urls?: string[];
  date_of_loss?: string | null;
  statute_of_limitations_date?: string | null;
  services?: MatterServices[];
  contact_client_directly?: boolean | null;
  city?: string | null;
  state?: string | null;
  representing_attorney?: (string | string) | null;
  external_cms_url?: string | null;
  cms_email?: string | null;
  referral_source_description?: string | null;
  intake_id?: (string | string) | null;
  last_contacted_at_override?: string | null;
  simplified_billing_code?: SimplifiedBillingCode;
  task_config?: TaskConfigSchema;
};
export type MatterCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type DiscriminatedMatterCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_created";
  payload: MatterCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
  diff: {
    [key: string]: any;
  };
  previous?: MatterSchema;
};
export type DiscriminatedMatterUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_updated";
  payload: MatterUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
  old_status: MatterStatus;
  new_status: MatterStatus;
};
export type DiscriminatedMatterStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_status_changed";
  payload: MatterStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterRetainerSignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type DiscriminatedMatterRetainerSignedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_retainer_signed";
  payload: MatterRetainerSignedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterRetainerSignedByClientEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type DiscriminatedMatterRetainerSignedByClientEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_retainer_signed_by_client";
  payload: MatterRetainerSignedByClientEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterRetainerSignedByAttorneyEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
};
export type DiscriminatedMatterRetainerSignedByAttorneyEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_retainer_signed_by_attorney";
  payload: MatterRetainerSignedByAttorneyEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type AssigneeRole =
  | "managing_paralegal"
  | "case_manager"
  | "police_report_manager"
  | "medical_records_manager"
  | "demands_team"
  | "benefits_specialist"
  | "claims_team"
  | "acknowledgement_team"
  | "treatment_team"
  | "draft_demand_team"
  | "finalize_demand_team"
  | "demand_readiness_team"
  | "liens_team";
export type UserSimpleSchema = {
  id: string | string;
  display_name: string;
  email: string;
};
export type MatterRoleAssignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
  role: AssigneeRole;
  assignee_id: string | string;
  assignee: UserSimpleSchema;
  tasks_reassigned_count?: number;
};
export type DiscriminatedMatterRoleAssignedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_role_assigned";
  payload: MatterRoleAssignedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterRoleChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
  role: AssigneeRole;
  old_assignee_id: string | string;
  old_assignee: UserSimpleSchema;
  new_assignee_id: string | string;
  new_assignee: UserSimpleSchema;
  tasks_reassigned_count?: number;
};
export type DiscriminatedMatterRoleChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_role_changed";
  payload: MatterRoleChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterRoleRemovedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  matter: MatterSchema;
  role: AssigneeRole;
  old_assignee_id: string | string;
  old_assignee: UserSimpleSchema;
  tasks_reassigned_count?: number;
};
export type DiscriminatedMatterRoleRemovedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_role_removed";
  payload: MatterRoleRemovedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PoliceReportCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  needs_approval: boolean;
};
export type DiscriminatedPoliceReportCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "police_report_created";
  payload: PoliceReportCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PoliceReportUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  diff: {
    [key: string]: any;
  };
  previous?: ReadPoliceReport;
};
export type DiscriminatedPoliceReportUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "police_report_updated";
  payload: PoliceReportUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PoliceReportStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  police_report: ReadPoliceReport;
  old_report_status: PoliceReportAttestationStatus;
  new_report_status: PoliceReportAttestationStatus;
};
export type DiscriminatedPoliceReportStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "police_report_status_changed";
  payload: PoliceReportStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterInvestigationStatus =
  | "need_info"
  | "researching"
  | "investigating"
  | "escalated"
  | "completed_report_received"
  | "closed_no_report"
  | "not_applicable"
  | "backfilled_closed";
export type ReadMatterInvestigation = {
  /** Overall investigation status for the matter */
  status?: MatterInvestigationStatus;
  /** Free-form notes explaining the current status or transitions */
  description?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
};
export type MatterInvestigationCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  investigation: ReadMatterInvestigation;
};
export type DiscriminatedMatterInvestigationCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_investigation_created";
  payload: MatterInvestigationCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterInvestigationUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  investigation: ReadMatterInvestigation;
  diff: {
    [key: string]: any;
  };
  previous?: ReadMatterInvestigation;
};
export type DiscriminatedMatterInvestigationUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_investigation_updated";
  payload: MatterInvestigationUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type StaffNoteCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  staff_note: StaffNoteSchema;
  matter_id: string | string;
  content: string;
};
export type DiscriminatedStaffNoteCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "staff_note_created";
  payload: StaffNoteCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type StaffNoteUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  staff_note: StaffNoteSchema;
  matter_id: string | string;
  content: string;
  previous: StaffNoteSchema;
};
export type DiscriminatedStaffNoteUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "staff_note_updated";
  payload: StaffNoteUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type StaffNoteDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  staff_note: StaffNoteSchema;
  matter_id: string | string;
};
export type DiscriminatedStaffNoteDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "staff_note_deleted";
  payload: StaffNoteDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskTemplateType =
  | "intake_conduct_intake"
  | "intake_conduct_initial_review"
  | "intake_signed_retainer"
  | "intake_signed_hipaa"
  | "intake_health_insurance_card"
  | "intake_declaration_page"
  | "intake_update_external_cms"
  | "intake_welcome_call"
  | "initial_review_client_drivers_license"
  | "initial_review_defendant_drivers_license"
  | "initial_review_client_auto_insurance"
  | "initial_review_request_supplemental"
  | "police_locate_police_report"
  | "police_retrieve_police_report"
  | "police_confirm_found_report"
  | "police_eliminated_pd"
  | "police_provide_missing_info"
  | "police_not_applicable"
  | "police_researching"
  | "police_unverified"
  | "police_verified"
  | "police_manual_intervention_required"
  | "police_confirm_no_report_exists"
  | "police_report_v2"
  | "police_foia_request"
  | "police_incident_report"
  | "insurance_1p_open_1p_claim"
  | "insurance_1p_send_1p_lor"
  | "insurance_1p_receive_1p_acknowledgement"
  | "insurance_1p_get_liability_limits"
  | "insurance_1p_declaration_page"
  | "insurance_3p_open_3p_claim"
  | "insurance_3p_send_3p_lor"
  | "insurance_3p_receive_3p_acknowledgement"
  | "insurance_3p_get_liability_limits"
  | "liens_open_file"
  | "liens_final_lien"
  | "liens_receive_lien_acknowledgement"
  | "liens_find_health_insurance_coverage"
  | "demands_draft_demand"
  | "demands_finalize_demand"
  | "demands_demand_readiness_review"
  | "treatment_treatment_check_in"
  | "treatment_resolve_barrier"
  | "treatment_requests_submit_request"
  | "medical_provider_review_found_provider"
  | "medical_provider_request_medical_records"
  | "treatment_record_request"
  | "medical_provider_request_medical_bills"
  | "medical_provider_request_affidavit"
  | "coverage_review_found_coverage"
  | "coverage_find_1p_coverage"
  | "coverage_find_3p_coverage"
  | "coverage_review_1p_benefits"
  | "coverage_pip_medpay_application"
  | "coverage_check_for_pip_ledger"
  | "coverage_ime_required"
  | "coverage_generate_no_medpay_letter"
  | "coverage_manage_medpay"
  | "coverage_provide_pip_info_to_provider"
  | "premises_confirm_incident_location"
  | "premises_research_property_owners"
  | "premises_send_lor"
  | "escalation_case_escalation";
export type TaskSchema = {
  id: string | string;
  matter_id?: (string | string) | null;
  name: string;
  description: string;
  group: TaskGroups;
  type: TaskType;
  due_date?: string | null;
  medical_note_id?: (string | string) | null;
  coverage_id?: (string | string) | null;
  police_report_id?: (string | string) | null;
  treatment_record_request_id?: (string | string) | null;
  external_cms_update_status?: TaskExternalCmsUpdateStatus;
  external_cms_update_reason?: string | null;
  assignee_id?: (string | string) | null;
  created_by_id?: (string | string) | null;
  template_type?: TaskTemplateType;
};
export type TaskCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
};
export type DiscriminatedTaskCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_created";
  payload: TaskCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskStatus =
  | "Pending"
  | "Blocked"
  | "In Progress"
  | "Completed"
  | "Not Applicable"
  | "Closed";
export type TaskStatusSchema = {
  id: string | string;
  name: string;
  canonical_status: TaskStatus;
  description?: string | null;
};
export type TaskStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: TaskStatusSchema;
  new?: TaskStatusSchema;
};
export type DiscriminatedTaskStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_status_changed";
  payload: TaskStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskAssignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: CustomUserSchema;
  new?: CustomUserSchema;
};
export type DiscriminatedTaskAssignedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_assigned";
  payload: TaskAssignedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskDueDateChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: string | null;
  new?: string | null;
};
export type DiscriminatedTaskDueDateChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_due_date_changed";
  payload: TaskDueDateChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskPriorityChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: number | null;
  new?: number | null;
};
export type DiscriminatedTaskPriorityChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_priority_changed";
  payload: TaskPriorityChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskNameChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: string | null;
  new?: string | null;
};
export type DiscriminatedTaskNameChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_name_changed";
  payload: TaskNameChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskDescriptionChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old?: string | null;
  new?: string | null;
};
export type DiscriminatedTaskDescriptionChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_description_changed";
  payload: TaskDescriptionChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
};
export type DiscriminatedTaskDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_deleted";
  payload: TaskDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskUpdateCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
};
export type DiscriminatedTaskUpdateCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_update_created";
  payload: TaskUpdateCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type UpdateTaskUpdateSchema = {
  /** The update to the task. This is a free-form text field that can be used to add any relevant information. */
  update?: string | null;
  /** Whether the update is visible to the customer, or internal to the operations staff. */
  visibility?: TaskUpdateVisibility;
  /** A list of Blob ids attached to the update. */
  attachments?: (string | string)[] | null;
  /** The user id of the user who created the update. If not provided, the update will be attributed to the system. */
  user_id?: (string | string) | null;
  agent?: AgentType;
  /** The type of update. This is used to determine the type of update to create. */
  update_type?: TaskUpdateType;
};
export type TaskUpdateUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
  diff: UpdateTaskUpdateSchema;
  previous?: TaskUpdateSchema;
};
export type DiscriminatedTaskUpdateUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_update_updated";
  payload: TaskUpdateUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskUpdateDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task_update: TaskUpdateSchema;
};
export type DiscriminatedTaskUpdateDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_update_deleted";
  payload: TaskUpdateDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TaskExternalCmsUpdateStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  old: TaskExternalCmsUpdateStatus;
  new: TaskExternalCmsUpdateStatus;
};
export type DiscriminatedTaskExternalCmsUpdateStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "task_external_cms_update_status_changed";
  payload: TaskExternalCmsUpdateStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type RainmakerSuggestionExecutedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  suggestion_id: string | string;
  suggestion_title: string;
  actions_summary: string[];
};
export type DiscriminatedRainmakerSuggestionExecutedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "rainmaker_suggestion_executed";
  payload: RainmakerSuggestionExecutedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  needs_approval: boolean;
};
export type DiscriminatedProviderCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_created";
  payload: ProviderCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  old_status: ProviderStatus;
  new_status: ProviderStatus;
};
export type DiscriminatedProviderStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_status_changed";
  payload: ProviderStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderTreatmentStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  old_treatment_status: ProviderTreatmentStatus;
  new_treatment_status: ProviderTreatmentStatus;
};
export type DiscriminatedProviderTreatmentStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_treatment_status_changed";
  payload: ProviderTreatmentStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 2;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentCheckInStatus =
  | "DRAFT"
  | "PROCESSING"
  | "NEEDS_REVIEW"
  | "COMPLETED";
export type TreatmentCheckInUrgencyLevel =
  | "ROUTINE"
  | "NEEDS_ATTENTION"
  | "URGENT";
export type TreatmentCheckInProviderUpdateSchema = {
  check_in_id: string | string;
  provider_id?: (string | string) | null;
  provider_name?: string | null;
  new_treatment_status?: ProviderTreatmentStatus;
  new_treatment_status_occurred_at?: string | null;
  new_treatment_frequency?: TreatmentFrequency;
  new_treatment_progress?: TreatmentProgress;
  new_pain_level?: number | null;
  new_treatment_notes?: string | null;
  id: string | string;
  created_at: string;
  modified_at: string;
};
export type TreatmentCheckInContextSchema = {
  days_since_last_check_in?: number | null;
  days_since_loss?: number | null;
  missing_day_30_diagnostic_testing?: boolean | null;
  missing_last_appointment_dates_count?: number;
  procedure_keywords_detected?: string[];
};
export type TreatmentCheckInSchema = {
  id: string | string;
  created_at: string;
  modified_at: string;
  matter_id: string | string;
  user_id?: (string | string) | null;
  triggered_by_task_id?: (string | string) | null;
  triggered_by_task?: any | null;
  status: TreatmentCheckInStatus;
  general_notes: string;
  check_in_time: string | null;
  urgency_level: TreatmentCheckInUrgencyLevel;
  provider_updates?: TreatmentCheckInProviderUpdateSchema[] | null;
  context?: TreatmentCheckInContextSchema;
};
export type TreatmentCheckInSubmittedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  check_in: TreatmentCheckInSchema;
};
export type DiscriminatedTreatmentCheckInSubmittedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_check_in_submitted";
  payload: TreatmentCheckInSubmittedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentCheckInNoteLoggedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  general_notes: string;
};
export type DiscriminatedTreatmentCheckInNoteLoggedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_check_in_note_logged";
  payload: TreatmentCheckInNoteLoggedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type VoiceAiCallCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  call_id: string;
  task_id?: string | null;
  provider_name?: string | null;
  request_status?: string | null;
  summary?: string | null;
  work_log_entry?: string | null;
};
export type DiscriminatedVoiceAiCallCompletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "voice_ai_call_completed";
  payload: VoiceAiCallCompletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderCuratedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  action: string;
  diff?: {
    [key: string]: any;
  } | null;
};
export type DiscriminatedProviderCuratedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_curated";
  payload: ProviderCuratedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProvidersMergedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  source_provider_ids: (string | string)[];
};
export type DiscriminatedProvidersMergedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "providers_merged";
  payload: ProvidersMergedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderPhoneEnrichedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider: MedicalProviderSchema;
  phone_number: string;
  handle_id: number;
};
export type DiscriminatedProviderPhoneEnrichedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "provider_phone_enriched";
  payload: ProviderPhoneEnrichedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PortalSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a medical records request portal (MRO, Smartrequest, Chartswap) */
  channel_type?: "PORTAL";
  /** The uuid of the portal. */
  portal_id: string;
};
export type FaxContactHandle = {
  /** A contact handle for a fax number */
  handle_type?: "FAX";
  /** Should contain an E.164 formatted fax number. */
  value: string;
};
export type FaxSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a fax number */
  channel_type?: "FAX";
  /** The handle of the submission channel. E.g. 'fax number' as well as the value */
  handle: FaxContactHandle;
};
export type EmailContactHandle = {
  /** A contact handle for an email address */
  handle_type?: "EMAIL";
  /** Should contain an email address. */
  value: string;
};
export type EmailSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through an email address */
  channel_type?: "EMAIL";
  /** The handle of the submission channel. E.g. 'email address' as well as the value */
  handle: EmailContactHandle;
};
export type MailingAddressContactHandle = {
  /** A contact handle for a mailing address */
  handle_type?: "MAILING_ADDRESS";
  /** The recipient of the mailing address. */
  recipient: string;
  /** The first line of the mailing address. */
  address_line_1: string;
  /** The second line of the mailing address. */
  address_line_2?: string | null;
  /** The city of the mailing address. */
  city: string;
  /** The state of the mailing address. */
  state: string;
  /** The zip code of the mailing address. */
  zip_code: string;
  /** The country of the mailing address. */
  country: string;
};
export type MailingAddressSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a mailing address */
  channel_type?: "MAILING_ADDRESS";
  /** The handle of the submission channel. E.g. 'mailing address' as well as the value */
  handle: MailingAddressContactHandle;
};
export type WebsiteSubmissionChannel = {
  /** Whether this is the preferred submission channel for the provider */
  is_preferred: boolean;
  /** The submission channel is through a website */
  channel_type?: "WEBSITE";
  /** The handle of the submission channel. E.g. 'website' as well as the value */
  handle: string;
  /** The url of the website of the submission channel. */
  website: string;
};
export type SubmissionMethod = {
  /** The method by which we can submit records to the provider */
  submission_method:
    | PortalSubmissionChannel
    | FaxSubmissionChannel
    | EmailSubmissionChannel
    | MailingAddressSubmissionChannel
    | WebsiteSubmissionChannel;
  /** A short and concise explanation of why this submission method is a good candidate for requesting records. Write it as you would explain it to a regular person. */
  reason: string;
  /** A score between 0 and 1 indicating the confidence in the submission method  */
  confidence: number;
  /** The scope (records we can expect to receive) for the submission method */
  scope?: TreatmentRecordTypes[] | null;
  /** Any special instructions for submitting records to the provider */
  submission_instructions?: string | null;
};
export type TreatmentRequestResearchCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  provider_id: string | string;
  submission_methods: SubmissionMethod[];
  selected_submission_methods: SubmissionMethod[];
};
export type DiscriminatedTreatmentRequestResearchCompletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_research_completed";
  payload: TreatmentRequestResearchCompletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentRequestCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
};
export type DiscriminatedTreatmentRequestCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_created";
  payload: TreatmentRequestCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type UpdateTreatmentRecordRequestSchema = {
  request_id: string | string;
  actor?: TreatmentRecordRequestActor;
  is_certified?: boolean | null;
  is_urgent?: boolean | null;
  provided_records?: TreatmentRecordTypes[] | null;
  status?: TreatmentRecordRequestStatus;
  metadata?: TreatmentRecordRequestMetadataSchema;
};
export type TreatmentRequestUpdatedEventV1 = {
  /** PK of the row that changed */
  target_id: string | string;
  diff: UpdateTreatmentRecordRequestSchema;
};
export type DiscriminatedTreatmentRequestUpdatedEventV1 = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_updated";
  payload: TreatmentRequestUpdatedEventV1;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentRequestUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  diff: UpdateTreatmentRecordRequestSchema;
  previous: TreatmentRecordRequestSchema;
};
export type DiscriminatedTreatmentRequestUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_updated";
  payload: TreatmentRequestUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 2;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentRequestDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
};
export type DiscriminatedTreatmentRequestDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_deleted";
  payload: TreatmentRequestDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type Reason =
  | "treatment_completed"
  | "request_submitted_via_portal"
  | "request_submitted_via_mail"
  | "request_submitted_via_fax"
  | "request_submitted_via_email"
  | "request_updated_via_voice_ai"
  | "request_updated_via_portal"
  | "invoice_paid_via_portal"
  | "records_ingested_via_portal"
  | "manual";
export type TreatmentRequestStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  old_status: TreatmentRecordRequestStatus;
  new_status: TreatmentRecordRequestStatus;
  reason?: Reason;
};
export type DiscriminatedTreatmentRequestStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_status_changed";
  payload: TreatmentRequestStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type TreatmentRequestFollowupSentEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  package_id: string | string;
  channel: string;
  triggered_by: string;
};
export type DiscriminatedTreatmentRequestFollowupSentEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "treatment_request_followup_sent";
  payload: TreatmentRequestFollowupSentEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type HipaaPackageSubmissionTrackingSchema = {
  provider_name?: string | null;
  medical_provider_id?: (string | string) | null;
  /** Timestamp when the HIPAA package was actually transmitted */
  sent_at?: string | null;
  fax_transmission_id?: string | null;
  recipient_number?: string | null;
  recipient_email?: string | null;
  attachment_filename?: string | null;
  front_conversation_id?: string | null;
  front_message_id?: string | null;
  front_conversation_url?: string | null;
  front_channel_id?: string | null;
  firm_id?: string | null;
  email_subject?: string | null;
  postgrid_letter_id?: string | null;
  postgrid_cheque_id?: string | null;
  letter_status?: string | null;
  cheque_status?: string | null;
  recipient_address?: string | null;
  include_cheque?: boolean | null;
  tracking_number?: string | null;
  local_letter_id?: string | null;
  local_cheque_id?: string | null;
};
export type HipaaPackageSubmittedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  treatment_request: TreatmentRecordRequestSchema;
  package_id: string | string;
  channel: string;
  tracking: HipaaPackageSubmissionTrackingSchema;
  workflow_id?: string | null;
};
export type DiscriminatedHipaaPackageSubmittedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "hipaa_package_submitted";
  payload: HipaaPackageSubmittedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type VoiceAgentRunUnblockedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  task: TaskSchema;
  previous_status?: string | null;
  new_status?: string | null;
};
export type DiscriminatedVoiceAgentRunUnblockedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "voice_ai_run_unblocked";
  payload: VoiceAgentRunUnblockedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CaseExpenseType = "POLICE" | "MEDICAL" | "MAIL" | "OTHER";
export type CaseExpenseStatus = "UNPAID" | "SENT" | "PAID" | "CANCELLED";
export type PaymentMethod = "CREDIT_CARD" | "CHEQUE" | "FIRM_PAYS";
export type LabeledPhone = {
  /** Label like 'mobile', 'home', 'work', 'main', 'fax' */
  label: string;
  /** Phone number */
  value: string;
};
export type LabeledEmail = {
  /** Label like 'home', 'work', 'other' */
  label: string;
  /** Email address */
  value: string;
};
export type LabeledPostalAddress = {
  /** Label like 'home', 'work', 'billing', 'shipping' */
  label: string;
  /** Street address (can include multiple lines) */
  street?: string | null;
  /** City */
  city?: string | null;
  /** State/Province */
  state?: string | null;
  /** Postal/ZIP code */
  postal_code?: string | null;
  /** Country */
  country?: string | null;
};
export type SimpleContactInfo = {
  /** Organization/company name */
  organization_name?: string | null;
  /** First name (for individuals) */
  given_name?: string | null;
  /** Last name (for individuals) */
  family_name?: string | null;
  /** Phone numbers with labels (mobile, home, work, main, fax) */
  phone_numbers?: LabeledPhone[];
  /** Email addresses with labels (home, work, other) */
  email_addresses?: LabeledEmail[];
  /** Postal addresses with labels (home, work, billing, shipping) */
  postal_addresses?: LabeledPostalAddress[];
  /** Additional notes about this contact */
  note?: string | null;
};
export type ContactPayee = {
  type?: "CONTACT";
  contact_info: SimpleContactInfo;
};
export type CoveragePayee = {
  type?: "COVERAGE";
  id: string | string;
  carrier_name: string | null;
  category: CoverageCategory;
};
export type PoliceDepartmentPayee = {
  type?: "POLICE_DEPARTMENT";
  id: string | string;
  name: string;
};
export type ProviderPayee = {
  type?: "PROVIDER";
  id: string | string;
  name: string;
};
export type BlobSummary = {
  id: string | string;
  name: string;
};
export type ReadCaseExpense = {
  id: string | string;
  matter_id: string | string;
  expense_type: CaseExpenseType;
  status: CaseExpenseStatus;
  payment_method?: PaymentMethod;
  payment_method_identifier?: string | null;
  payment_date?: string | null;
  amount: string;
  reason?: string | null;
  payee:
    | ({
        type: "CONTACT";
      } & ContactPayee)
    | ({
        type: "COVERAGE";
      } & CoveragePayee)
    | ({
        type: "POLICE_DEPARTMENT";
      } & PoliceDepartmentPayee)
    | ({
        type: "PROVIDER";
      } & ProviderPayee);
  blobs?: BlobSummary[];
  created_at: string;
  modified_at: string;
};
export type CaseExpenseCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  case_expense: ReadCaseExpense;
};
export type DiscriminatedCaseExpenseCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "case_expense_created";
  payload: CaseExpenseCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CaseExpenseUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  case_expense: ReadCaseExpense;
  diff: {
    [key: string]: any;
  };
  previous?: ReadCaseExpense;
};
export type DiscriminatedCaseExpenseUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "case_expense_updated";
  payload: CaseExpenseUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CaseExpenseDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  case_expense: ReadCaseExpense;
};
export type DiscriminatedCaseExpenseDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "case_expense_deleted";
  payload: CaseExpenseDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterCompanionPrimarySetEvent = {
  /** PK of the row that changed */
  target_id: string | string;
};
export type DiscriminatedMatterCompanionPrimarySetEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_companion_primary_set";
  payload: MatterCompanionPrimarySetEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterCompanionMembersAddedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  primary_matter_id: string | string;
  added_member_ids: (string | string)[];
};
export type DiscriminatedMatterCompanionMembersAddedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_companion_members_added";
  payload: MatterCompanionMembersAddedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type MatterCompanionMembersRemovedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  primary_matter_id: string | string;
  removed_member_ids: (string | string)[];
};
export type DiscriminatedMatterCompanionMembersRemovedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "matter_companion_members_removed";
  payload: MatterCompanionMembersRemovedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CorrespondencePackageType =
  | "hipaa_authorization"
  | "foia_request"
  | "lien_notice"
  | "demand_package"
  | "follow_up"
  | "subpoena"
  | "premises_lor"
  | "custom";
export type CorrespondencePackageCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  package_id?: (string | string) | null;
  matter_id: string | string;
  package_type: CorrespondencePackageType;
  treatment_request_id?: (string | string) | null;
  investigation_request_id?: (string | string) | null;
};
export type DiscriminatedCorrespondencePackageCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "correspondence_package_created";
  payload: CorrespondencePackageCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type RenderStatus =
  | "draft"
  | "missing_fields"
  | "queued_for_render"
  | "generating"
  | "ready"
  | "published"
  | "error";
export type CorrespondencePackageStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  package_id?: (string | string) | null;
  matter_id: string | string;
  old_status?: RenderStatus;
  new_status: RenderStatus;
};
export type DiscriminatedCorrespondencePackageStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "correspondence_package_status_changed";
  payload: CorrespondencePackageStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ContactCardType =
  | "medical_provider"
  | "police_department"
  | "insurance_carrier"
  | "party"
  | "custom";
export type CorrespondencePackageSentEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  package_id?: (string | string) | null;
  matter_id: string | string;
  message_id: string | string;
  channel: MessageType;
  entity_type?: ContactCardType;
  entity_id?: (string | string) | null;
};
export type DiscriminatedCorrespondencePackageSentEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "correspondence_package_sent";
  payload: CorrespondencePackageSentEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type CorrespondenceMessageTransmissionStatusChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  message_id: string | string;
  matter_id: string | string;
  old_status?: TransmissionStatus;
  new_status: TransmissionStatus;
  channel?: MessageType;
  error_message?: string | null;
};
export type DiscriminatedCorrespondenceMessageTransmissionStatusChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "correspondence_message_transmission_status_changed";
  payload: CorrespondenceMessageTransmissionStatusChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type RuleEvaluationStatus = "MATCHED" | "NO_MATCH";
export type TriggerTaskType = "EXTERNAL" | "INTERNAL";
export type RuleEvaluationSchema = {
  id: string | string;
  rule_version_id: string | string;
  internal_task_id?: (string | string) | null;
  external_task_id?: (string | string) | null;
  workflow_id?: string | null;
  evaluation_status: RuleEvaluationStatus;
  links_created: number;
  evaluated_at: string;
  created_at: string;
  /** Backward compatibility: derive task type from FK fields. */
  trigger_task_type: TriggerTaskType;
};
export type RuleEvaluationSchemaRead = {
  id: string | string;
  rule_version_id: string | string;
  internal_task_id?: (string | string) | null;
  external_task_id?: (string | string) | null;
  workflow_id?: string | null;
  evaluation_status: RuleEvaluationStatus;
  links_created: number;
  evaluated_at: string;
  created_at: string;
  /** Backward compatibility: derive task type from FK fields. */
  trigger_task_type: TriggerTaskType;
  /** Backward compatibility: derive task ID from FK fields. */
  trigger_task_id: (string | string) | null;
};
export type ExternalTaskRuleEvaluationCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  rule_evaluation: RuleEvaluationSchema;
  rule_id: string | string;
  rule_name: string;
};
export type ExternalTaskRuleEvaluationCreatedEventRead = {
  /** PK of the row that changed */
  target_id: string | string;
  rule_evaluation: RuleEvaluationSchemaRead;
  rule_id: string | string;
  rule_name: string;
};
export type DiscriminatedExternalTaskRuleEvaluationCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "external_task_rule_evaluation_created";
  payload: ExternalTaskRuleEvaluationCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DiscriminatedExternalTaskRuleEvaluationCreatedEventRead = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "external_task_rule_evaluation_created";
  payload: ExternalTaskRuleEvaluationCreatedEventRead;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type HipaaAuthorizationSignatureUploadedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  authorization_id?: (string | string) | null;
  client_id: string | string;
  signature_date: string;
  file_name?: string | null;
  file_size?: number | null;
};
export type DiscriminatedHipaaAuthorizationSignatureUploadedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "hipaa_authorization_signature_uploaded";
  payload: HipaaAuthorizationSignatureUploadedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ProviderSummary = {
  id: string | string;
  name: string;
};
export type AllocationBucket =
  | "CLIENT_SELF_PAYMENT"
  | "PIP_PAYMENT"
  | "MEDPAY_PAYMENT"
  | "HEALTH_INSURANCE_REIMBURSEMENT"
  | "PROVIDER_DEFERRED_PAYMENT"
  | "UNALLOCATED";
export type PayerType = "PROVIDER" | "COVERAGE" | "CLIENT";
export type CoverageSummary = {
  id: string | string;
  carrier_name: string | null;
  category: CoverageCategory;
};
export type ReadAllocation = {
  id: string | string;
  allocation_bucket: AllocationBucket;
  payer_type?: PayerType;
  payer_coverage?: CoverageSummary;
  payer_provider?: ProviderSummary;
  amount: string;
};
export type ReadExpense = {
  id: string | string;
  matter_id: string | string;
  provider_id: string | string;
  provider: ProviderSummary;
  service_date: string;
  billed_amount: string;
  description: string;
  related_to_incident: boolean;
  managed_by_system: string | null;
  external_id: string | null;
  allocations: ReadAllocation[];
  is_balanced: boolean;
  created_at: string;
  modified_at: string;
};
export type ExpenseCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
};
export type DiscriminatedExpenseCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "expense_created";
  payload: ExpenseCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ExpenseUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
  diff: {
    [key: string]: any;
  };
  previous?: ReadExpense;
};
export type DiscriminatedExpenseUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "expense_updated";
  payload: ExpenseUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ExpenseDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
};
export type DiscriminatedExpenseDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "expense_deleted";
  payload: ExpenseDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type AllocationsReplacedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  expense: ReadExpense;
  previous_allocations: ReadAllocation[];
  new_allocations: ReadAllocation[];
};
export type DiscriminatedAllocationsReplacedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "allocations_replaced";
  payload: AllocationsReplacedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LienholderType = "PROVIDER" | "COVERAGE";
export type LienType = "STATUTORY" | "CONTRACTUAL";
export type LienStatus = "ACTIVE" | "RESOLVED";
export type ReadLien = {
  id: string | string;
  matter_id: string | string;
  lienholder_type: LienholderType;
  lienholder_coverage?: CoverageSummary;
  lienholder_provider?: ProviderSummary;
  lien_type: LienType;
  status: LienStatus;
  exposure_amount: string;
  created_at: string;
  modified_at: string;
};
export type LienCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  lien: ReadLien;
};
export type DiscriminatedLienCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "lien_created";
  payload: LienCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LienUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  lien: ReadLien;
  diff: {
    [key: string]: any;
  };
  previous?: ReadLien;
};
export type DiscriminatedLienUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "lien_updated";
  payload: LienUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LienDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  lien: ReadLien;
};
export type DiscriminatedLienDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "lien_deleted";
  payload: LienDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaOutreachStatus =
  | "running"
  | "paused"
  | "completed_loa_received"
  | "completed_rejected"
  | "stopped_manually"
  | "monthly_cadence";
export type LoaOutreachStepType =
  | "auto_email"
  | "auto_fax"
  | "manual_call"
  | "manual_email"
  | "supervisor_escalation";
export type LoaOutreachCampaignSchema = {
  id: string | string;
  matter_id: string | string;
  coverage_id: string | string;
  workflow_id: string;
  status: LoaOutreachStatus;
  current_step_index: number;
  current_step_label: string;
  next_action_date?: string | null;
  next_action_type?: LoaOutreachStepType;
  next_action_label?: string;
  last_contact_date?: string | null;
  last_contact_type?: LoaOutreachStepType;
  total_contact_count?: number;
  email_thread_id?: string;
  stopped_reason?: string | null;
  created_at: string;
  modified_at: string;
};
export type LoaChaserCampaignStartedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  starting_step: string;
  email_thread_id: string;
};
export type DiscriminatedLoaChaserCampaignStartedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_started";
  payload: LoaChaserCampaignStartedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignPausedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
};
export type DiscriminatedLoaChaserCampaignPausedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_paused";
  payload: LoaChaserCampaignPausedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignResumedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
};
export type DiscriminatedLoaChaserCampaignResumedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_resumed";
  payload: LoaChaserCampaignResumedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignStoppedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  reason?: string | null;
};
export type DiscriminatedLoaChaserCampaignStoppedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_stopped";
  payload: LoaChaserCampaignStoppedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserCampaignCompletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  outcome: LoaOutreachStatus;
};
export type DiscriminatedLoaChaserCampaignCompletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_campaign_completed";
  payload: LoaChaserCampaignCompletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserStepExecutedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  step_id: string;
  step_label: string;
  step_type: LoaOutreachStepType;
  was_auto?: boolean;
};
export type DiscriminatedLoaChaserStepExecutedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_step_executed";
  payload: LoaChaserStepExecutedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserStepChangedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  previous_step_id: string;
  previous_step_label: string;
  new_step_id: string;
  new_step_label: string;
  /** 'skip' (forward) or 'reset' (backward) */
  direction: string;
};
export type DiscriminatedLoaChaserStepChangedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_step_changed";
  payload: LoaChaserStepChangedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserManualActivityLoggedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  activity_type: LoaOutreachStepType;
  notes: string;
  executed_at: string;
};
export type DiscriminatedLoaChaserManualActivityLoggedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_manual_activity_logged";
  payload: LoaChaserManualActivityLoggedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserScheduleRealignedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  previous_next_step: string;
  previous_due_date: string;
  new_next_step: string;
  new_due_date: string;
  trigger: string;
};
export type DiscriminatedLoaChaserScheduleRealignedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_schedule_realigned";
  payload: LoaChaserScheduleRealignedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type LoaChaserSnoozedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  campaign: LoaOutreachCampaignSchema;
  snooze_days: number;
  previous_due_date: string;
  new_due_date: string;
};
export type DiscriminatedLoaChaserSnoozedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "loa_chaser_snoozed";
  payload: LoaChaserSnoozedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type NotebookSchema = {
  id: string | string;
  user_id: string | string;
  title: string;
  content: string;
  position: number;
  is_archived: boolean;
  created_at: string;
  modified_at: string;
};
export type NotebookCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  user_id: string | string;
  title: string;
};
export type DiscriminatedNotebookCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "notebook_created";
  payload: NotebookCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type NotebookUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  user_id: string | string;
  diff?: {
    [key: string]: any;
  } | null;
};
export type DiscriminatedNotebookUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "notebook_updated";
  payload: NotebookUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type NotebookDeletedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  user_id: string | string;
};
export type DiscriminatedNotebookDeletedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "notebook_deleted";
  payload: NotebookDeletedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type NotebookMatterLinkedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  matter_id: string | string;
  linked_by_id: (string | string) | null;
};
export type DiscriminatedNotebookMatterLinkedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "notebook_matter_linked";
  payload: NotebookMatterLinkedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type NotebookMatterUnlinkedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  notebook: NotebookSchema;
  matter_id: string | string;
  unlinked_by_id: (string | string) | null;
};
export type DiscriminatedNotebookMatterUnlinkedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "notebook_matter_unlinked";
  payload: NotebookMatterUnlinkedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PartyType = "person" | "organization";
export type IdentityStatus = "named" | "described" | "unknown";
export type FieldSuggestionMetadata = {
  /** The field name that was accepted (e.g., 'first_name', 'ssn'). */
  field_key: string;
  /** ID of the user who accepted this field. */
  set_by_user_id?: (string | string) | null;
  /** Timestamp when this field was accepted. */
  set_at?: string | null;
};
export type EntitySidecar = {
  /** List of individually accepted fields. */
  field_suggestions: FieldSuggestionMetadata[];
};
export type ReadPerson = {
  party_type?: PartyType;
  is_claimant?: boolean;
  identity_status?: IdentityStatus;
  short_description?: string | null;
  involvement_description?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  first_name?: string | null;
  middle_name?: string | null;
  last_name?: string | null;
  suffix?: string | null;
  ssn?: string | null;
  date_of_birth?: string | null;
  stated_age?: number | null;
  is_minor?: boolean;
  gender?: string | null;
  preferred_language?: string | null;
  drivers_license_number?: string | null;
  drivers_license_state_code?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  contact_handles?: ContactHandleSchema[];
  approval_metadata?: EntitySidecar;
};
export type PersonCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  person: ReadPerson;
  needs_approval: boolean;
};
export type DiscriminatedPersonCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "person_created";
  payload: PersonCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PersonUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  person: ReadPerson;
  previous?: ReadPerson;
};
export type DiscriminatedPersonUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "person_updated";
  payload: PersonUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type OrganizationType =
  | "corporation"
  | "trust"
  | "partnership"
  | "llc"
  | "llp"
  | "other";
export type ReadOrganization = {
  party_type?: PartyType;
  is_claimant?: boolean;
  identity_status?: IdentityStatus;
  short_description?: string | null;
  involvement_description?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  legal_name?: string | null;
  dba_name?: string | null;
  organization_type: OrganizationType;
  tax_id?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  contact_handles?: ContactHandleSchema[];
  approval_metadata?: EntitySidecar;
};
export type OrganizationCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  organization: ReadOrganization;
  needs_approval: boolean;
};
export type DiscriminatedOrganizationCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "organization_created";
  payload: OrganizationCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type OrganizationUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  organization: ReadOrganization;
  previous?: ReadOrganization;
};
export type DiscriminatedOrganizationUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "organization_updated";
  payload: OrganizationUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type ReadAnimal = {
  short_description?: string | null;
  involvement_description?: string | null;
  name?: string | null;
  species?: string | null;
  description?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  approval_metadata?: EntitySidecar;
};
export type AnimalCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  animal: ReadAnimal;
  needs_approval: boolean;
};
export type DiscriminatedAnimalCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "animal_created";
  payload: AnimalCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type AnimalUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  animal: ReadAnimal;
  previous?: ReadAnimal;
};
export type DiscriminatedAnimalUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "animal_updated";
  payload: AnimalUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PhysicalAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
};
export type RealPropertyType =
  | "residential"
  | "commercial"
  | "industrial"
  | "agricultural"
  | "mixed_use";
export type ReadProperty = {
  short_description?: string | null;
  involvement_description?: string | null;
  property_name?: string | null;
  physical_address?: PhysicalAddress;
  legal_description?: string | null;
  real_property_type: RealPropertyType;
  parcel_id?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  approval_metadata?: EntitySidecar;
};
export type PropertyCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  property: ReadProperty;
  needs_approval: boolean;
};
export type DiscriminatedPropertyCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "property_created";
  payload: PropertyCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type PropertyUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  property: ReadProperty;
  previous?: ReadProperty;
};
export type DiscriminatedPropertyUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "property_updated";
  payload: PropertyUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type VehicleType =
  | "passenger_car"
  | "truck"
  | "motorcycle"
  | "bus"
  | "bicycle"
  | "scooter"
  | "skateboard"
  | "roller_skate"
  | "roller_blade"
  | "trailer"
  | "other";
export type ReadVehicle = {
  short_description?: string | null;
  involvement_description?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  color?: string | null;
  vin?: string | null;
  license_plate?: string | null;
  license_plate_state_code?: string | null;
  vehicle_type: VehicleType;
  estimated_speed_mph?: number | null;
  label_in_police_report?: string | null;
  id: string | string;
  matter_id: string | string;
  created_at: string;
  modified_at: string;
  approval_metadata?: EntitySidecar;
};
export type VehicleCreatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  vehicle: ReadVehicle;
  needs_approval: boolean;
};
export type DiscriminatedVehicleCreatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "vehicle_created";
  payload: VehicleCreatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type VehicleUpdatedEvent = {
  /** PK of the row that changed */
  target_id: string | string;
  vehicle: ReadVehicle;
  previous?: ReadVehicle;
};
export type DiscriminatedVehicleUpdatedEvent = {
  matter_id: string | string;
  /** Unique event identifier */
  id?: string;
  occurred_at?: string;
  event_code: "vehicle_updated";
  payload: VehicleUpdatedEvent;
  correlation_id?: (string | string) | null;
  version: 1;
  user?: CustomUserSchema;
  agent?: AgentType;
};
export type DomainEventUpdateItemStatus =
  | "pending"
  | "transferred"
  | "skipped"
  | "filtered";
export type EventFilterReason = {
  filter_name: string;
  reasoning: string;
};
export type DomainEventUpdateItemDetailed = {
  id: string | string;
  event:
    | DiscriminatedArtifactCreatedEvent
    | DiscriminatedArtifactDigestCreatedEvent
    | DiscriminatedArtifactDigestStatusUpdatedEvent
    | DiscriminatedBlobCreatedEvent
    | DiscriminatedBlobDeletedEvent
    | DiscriminatedCoverageCreatedEvent
    | DiscriminatedCoverageUpdatedEvent
    | DiscriminatedCoverageStatusChangedEvent
    | DiscriminatedCoverageCategoryChangedEvent
    | DiscriminatedCoverageModificationBlockedEvent
    | DiscriminatedDataRequirementCreatedEvent
    | DiscriminatedDataRequirementStatusChangedEvent
    | DiscriminatedDataRequirementSatisfiedEvent
    | DiscriminatedDataRequirementValueCreatedEvent
    | DiscriminatedDataRequirementValueApprovedEvent
    | DiscriminatedDataRequirementValueStatusChangedEvent
    | DiscriminatedDeliverableCreatedEvent
    | DiscriminatedDeliverableStatusChangedEvent
    | DiscriminatedDeliverableSatisfiedEvent
    | DiscriminatedDeliverableMatchCreatedEvent
    | DiscriminatedDeliverableMatchStatusChangedEvent
    | DiscriminatedDeliverableMatchApprovedEvent
    | DiscriminatedDocumentCreatedEvent
    | DiscriminatedDocumentStatusChangedEvent
    | DiscriminatedDocumentDeletedEvent
    | DiscriminatedLoRGenerationAuditEvent
    | DiscriminatedHipaaGenerationAuditEvent
    | DiscriminatedLoRDataUpdatedEvent
    | DiscriminatedIncidentCreatedEvent
    | DiscriminatedIncidentUpdatedEvent
    | DiscriminatedIntakeCreatedEvent
    | DiscriminatedIntakeUpdatedEvent
    | DiscriminatedIntakeStatusChangedEvent
    | DiscriminatedMarkdownTransformCompletedEvent
    | DiscriminatedMatterClientCreatedEvent
    | DiscriminatedMatterClientUpdatedEvent
    | DiscriminatedMatterClientPhoneNumberAddedEvent
    | DiscriminatedMatterCreatedEvent
    | DiscriminatedMatterUpdatedEvent
    | DiscriminatedMatterStatusChangedEvent
    | DiscriminatedMatterRetainerSignedEvent
    | DiscriminatedMatterRetainerSignedByClientEvent
    | DiscriminatedMatterRetainerSignedByAttorneyEvent
    | DiscriminatedMatterRoleAssignedEvent
    | DiscriminatedMatterRoleChangedEvent
    | DiscriminatedMatterRoleRemovedEvent
    | DiscriminatedPoliceReportCreatedEvent
    | DiscriminatedPoliceReportUpdatedEvent
    | DiscriminatedPoliceReportStatusChangedEvent
    | DiscriminatedMatterInvestigationCreatedEvent
    | DiscriminatedMatterInvestigationUpdatedEvent
    | DiscriminatedStaffNoteCreatedEvent
    | DiscriminatedStaffNoteUpdatedEvent
    | DiscriminatedStaffNoteDeletedEvent
    | DiscriminatedTaskCreatedEvent
    | DiscriminatedTaskStatusChangedEvent
    | DiscriminatedTaskAssignedEvent
    | DiscriminatedTaskDueDateChangedEvent
    | DiscriminatedTaskPriorityChangedEvent
    | DiscriminatedTaskNameChangedEvent
    | DiscriminatedTaskDescriptionChangedEvent
    | DiscriminatedTaskDeletedEvent
    | DiscriminatedTaskUpdateCreatedEvent
    | DiscriminatedTaskUpdateUpdatedEvent
    | DiscriminatedTaskUpdateDeletedEvent
    | DiscriminatedTaskExternalCmsUpdateStatusChangedEvent
    | DiscriminatedRainmakerSuggestionExecutedEvent
    | DiscriminatedProviderCreatedEvent
    | DiscriminatedProviderStatusChangedEvent
    | DiscriminatedProviderTreatmentStatusChangedEvent
    | DiscriminatedTreatmentCheckInSubmittedEvent
    | DiscriminatedTreatmentCheckInNoteLoggedEvent
    | DiscriminatedVoiceAiCallCompletedEvent
    | DiscriminatedProviderCuratedEvent
    | DiscriminatedProvidersMergedEvent
    | DiscriminatedProviderPhoneEnrichedEvent
    | DiscriminatedTreatmentRequestResearchCompletedEvent
    | DiscriminatedTreatmentRequestCreatedEvent
    | DiscriminatedTreatmentRequestUpdatedEventV1
    | DiscriminatedTreatmentRequestUpdatedEvent
    | DiscriminatedTreatmentRequestDeletedEvent
    | DiscriminatedTreatmentRequestStatusChangedEvent
    | DiscriminatedTreatmentRequestFollowupSentEvent
    | DiscriminatedHipaaPackageSubmittedEvent
    | DiscriminatedVoiceAgentRunUnblockedEvent
    | DiscriminatedCaseExpenseCreatedEvent
    | DiscriminatedCaseExpenseUpdatedEvent
    | DiscriminatedCaseExpenseDeletedEvent
    | DiscriminatedMatterCompanionPrimarySetEvent
    | DiscriminatedMatterCompanionMembersAddedEvent
    | DiscriminatedMatterCompanionMembersRemovedEvent
    | DiscriminatedCorrespondencePackageCreatedEvent
    | DiscriminatedCorrespondencePackageStatusChangedEvent
    | DiscriminatedCorrespondencePackageSentEvent
    | DiscriminatedCorrespondenceMessageTransmissionStatusChangedEvent
    | DiscriminatedExternalTaskRuleEvaluationCreatedEvent
    | DiscriminatedHipaaAuthorizationSignatureUploadedEvent
    | DiscriminatedExpenseCreatedEvent
    | DiscriminatedExpenseUpdatedEvent
    | DiscriminatedExpenseDeletedEvent
    | DiscriminatedAllocationsReplacedEvent
    | DiscriminatedLienCreatedEvent
    | DiscriminatedLienUpdatedEvent
    | DiscriminatedLienDeletedEvent
    | DiscriminatedLoaChaserCampaignStartedEvent
    | DiscriminatedLoaChaserCampaignPausedEvent
    | DiscriminatedLoaChaserCampaignResumedEvent
    | DiscriminatedLoaChaserCampaignStoppedEvent
    | DiscriminatedLoaChaserCampaignCompletedEvent
    | DiscriminatedLoaChaserStepExecutedEvent
    | DiscriminatedLoaChaserStepChangedEvent
    | DiscriminatedLoaChaserManualActivityLoggedEvent
    | DiscriminatedLoaChaserScheduleRealignedEvent
    | DiscriminatedLoaChaserSnoozedEvent
    | DiscriminatedNotebookCreatedEvent
    | DiscriminatedNotebookUpdatedEvent
    | DiscriminatedNotebookDeletedEvent
    | DiscriminatedNotebookMatterLinkedEvent
    | DiscriminatedNotebookMatterUnlinkedEvent
    | DiscriminatedPersonCreatedEvent
    | DiscriminatedPersonUpdatedEvent
    | DiscriminatedOrganizationCreatedEvent
    | DiscriminatedOrganizationUpdatedEvent
    | DiscriminatedAnimalCreatedEvent
    | DiscriminatedAnimalUpdatedEvent
    | DiscriminatedPropertyCreatedEvent
    | DiscriminatedPropertyUpdatedEvent
    | DiscriminatedVehicleCreatedEvent
    | DiscriminatedVehicleUpdatedEvent;
  status: DomainEventUpdateItemStatus;
  completed_at: string | null;
  completed_by_id: (string | string) | null;
  filter_metadata: EventFilterReason;
  created_at: string;
  modified_at: string;
};
export type DomainEventUpdateItemDetailedRead = {
  id: string | string;
  event:
    | DiscriminatedArtifactCreatedEvent
    | DiscriminatedArtifactDigestCreatedEvent
    | DiscriminatedArtifactDigestStatusUpdatedEvent
    | DiscriminatedBlobCreatedEvent
    | DiscriminatedBlobDeletedEvent
    | DiscriminatedCoverageCreatedEvent
    | DiscriminatedCoverageUpdatedEvent
    | DiscriminatedCoverageStatusChangedEvent
    | DiscriminatedCoverageCategoryChangedEvent
    | DiscriminatedCoverageModificationBlockedEvent
    | DiscriminatedDataRequirementCreatedEvent
    | DiscriminatedDataRequirementStatusChangedEvent
    | DiscriminatedDataRequirementSatisfiedEvent
    | DiscriminatedDataRequirementValueCreatedEvent
    | DiscriminatedDataRequirementValueApprovedEvent
    | DiscriminatedDataRequirementValueStatusChangedEvent
    | DiscriminatedDeliverableCreatedEventRead
    | DiscriminatedDeliverableStatusChangedEventRead
    | DiscriminatedDeliverableSatisfiedEventRead
    | DiscriminatedDeliverableMatchCreatedEventRead
    | DiscriminatedDeliverableMatchStatusChangedEventRead
    | DiscriminatedDeliverableMatchApprovedEventRead
    | DiscriminatedDocumentCreatedEvent
    | DiscriminatedDocumentStatusChangedEvent
    | DiscriminatedDocumentDeletedEvent
    | DiscriminatedLoRGenerationAuditEvent
    | DiscriminatedHipaaGenerationAuditEvent
    | DiscriminatedLoRDataUpdatedEvent
    | DiscriminatedIncidentCreatedEvent
    | DiscriminatedIncidentUpdatedEvent
    | DiscriminatedIntakeCreatedEvent
    | DiscriminatedIntakeUpdatedEvent
    | DiscriminatedIntakeStatusChangedEvent
    | DiscriminatedMarkdownTransformCompletedEvent
    | DiscriminatedMatterClientCreatedEvent
    | DiscriminatedMatterClientUpdatedEvent
    | DiscriminatedMatterClientPhoneNumberAddedEvent
    | DiscriminatedMatterCreatedEvent
    | DiscriminatedMatterUpdatedEvent
    | DiscriminatedMatterStatusChangedEvent
    | DiscriminatedMatterRetainerSignedEvent
    | DiscriminatedMatterRetainerSignedByClientEvent
    | DiscriminatedMatterRetainerSignedByAttorneyEvent
    | DiscriminatedMatterRoleAssignedEvent
    | DiscriminatedMatterRoleChangedEvent
    | DiscriminatedMatterRoleRemovedEvent
    | DiscriminatedPoliceReportCreatedEvent
    | DiscriminatedPoliceReportUpdatedEvent
    | DiscriminatedPoliceReportStatusChangedEvent
    | DiscriminatedMatterInvestigationCreatedEvent
    | DiscriminatedMatterInvestigationUpdatedEvent
    | DiscriminatedStaffNoteCreatedEvent
    | DiscriminatedStaffNoteUpdatedEvent
    | DiscriminatedStaffNoteDeletedEvent
    | DiscriminatedTaskCreatedEvent
    | DiscriminatedTaskStatusChangedEvent
    | DiscriminatedTaskAssignedEvent
    | DiscriminatedTaskDueDateChangedEvent
    | DiscriminatedTaskPriorityChangedEvent
    | DiscriminatedTaskNameChangedEvent
    | DiscriminatedTaskDescriptionChangedEvent
    | DiscriminatedTaskDeletedEvent
    | DiscriminatedTaskUpdateCreatedEvent
    | DiscriminatedTaskUpdateUpdatedEvent
    | DiscriminatedTaskUpdateDeletedEvent
    | DiscriminatedTaskExternalCmsUpdateStatusChangedEvent
    | DiscriminatedRainmakerSuggestionExecutedEvent
    | DiscriminatedProviderCreatedEvent
    | DiscriminatedProviderStatusChangedEvent
    | DiscriminatedProviderTreatmentStatusChangedEvent
    | DiscriminatedTreatmentCheckInSubmittedEvent
    | DiscriminatedTreatmentCheckInNoteLoggedEvent
    | DiscriminatedVoiceAiCallCompletedEvent
    | DiscriminatedProviderCuratedEvent
    | DiscriminatedProvidersMergedEvent
    | DiscriminatedProviderPhoneEnrichedEvent
    | DiscriminatedTreatmentRequestResearchCompletedEvent
    | DiscriminatedTreatmentRequestCreatedEvent
    | DiscriminatedTreatmentRequestUpdatedEventV1
    | DiscriminatedTreatmentRequestUpdatedEvent
    | DiscriminatedTreatmentRequestDeletedEvent
    | DiscriminatedTreatmentRequestStatusChangedEvent
    | DiscriminatedTreatmentRequestFollowupSentEvent
    | DiscriminatedHipaaPackageSubmittedEvent
    | DiscriminatedVoiceAgentRunUnblockedEvent
    | DiscriminatedCaseExpenseCreatedEvent
    | DiscriminatedCaseExpenseUpdatedEvent
    | DiscriminatedCaseExpenseDeletedEvent
    | DiscriminatedMatterCompanionPrimarySetEvent
    | DiscriminatedMatterCompanionMembersAddedEvent
    | DiscriminatedMatterCompanionMembersRemovedEvent
    | DiscriminatedCorrespondencePackageCreatedEvent
    | DiscriminatedCorrespondencePackageStatusChangedEvent
    | DiscriminatedCorrespondencePackageSentEvent
    | DiscriminatedCorrespondenceMessageTransmissionStatusChangedEvent
    | DiscriminatedExternalTaskRuleEvaluationCreatedEventRead
    | DiscriminatedHipaaAuthorizationSignatureUploadedEvent
    | DiscriminatedExpenseCreatedEvent
    | DiscriminatedExpenseUpdatedEvent
    | DiscriminatedExpenseDeletedEvent
    | DiscriminatedAllocationsReplacedEvent
    | DiscriminatedLienCreatedEvent
    | DiscriminatedLienUpdatedEvent
    | DiscriminatedLienDeletedEvent
    | DiscriminatedLoaChaserCampaignStartedEvent
    | DiscriminatedLoaChaserCampaignPausedEvent
    | DiscriminatedLoaChaserCampaignResumedEvent
    | DiscriminatedLoaChaserCampaignStoppedEvent
    | DiscriminatedLoaChaserCampaignCompletedEvent
    | DiscriminatedLoaChaserStepExecutedEvent
    | DiscriminatedLoaChaserStepChangedEvent
    | DiscriminatedLoaChaserManualActivityLoggedEvent
    | DiscriminatedLoaChaserScheduleRealignedEvent
    | DiscriminatedLoaChaserSnoozedEvent
    | DiscriminatedNotebookCreatedEvent
    | DiscriminatedNotebookUpdatedEvent
    | DiscriminatedNotebookDeletedEvent
    | DiscriminatedNotebookMatterLinkedEvent
    | DiscriminatedNotebookMatterUnlinkedEvent
    | DiscriminatedPersonCreatedEvent
    | DiscriminatedPersonUpdatedEvent
    | DiscriminatedOrganizationCreatedEvent
    | DiscriminatedOrganizationUpdatedEvent
    | DiscriminatedAnimalCreatedEvent
    | DiscriminatedAnimalUpdatedEvent
    | DiscriminatedPropertyCreatedEvent
    | DiscriminatedPropertyUpdatedEvent
    | DiscriminatedVehicleCreatedEvent
    | DiscriminatedVehicleUpdatedEvent;
  status: DomainEventUpdateItemStatus;
  completed_at: string | null;
  completed_by_id: (string | string) | null;
  filter_metadata: EventFilterReason;
  created_at: string;
  modified_at: string;
};
export const {
  useCmsUpdatesListQuery,
  useCmsUpdatesMarkSkippedMutation,
  useCmsUpdatesMarkTransferredMutation,
} = injectedRtkApi;
